import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, isDevMode, signal } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { FeatureFlagService, TokenService } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { LogLevel, type LogEvent } from '@cudirect/sawdust/types';

import { RuntimeConfigService } from '@app/service/runtime-config.service';
import type { AppConfig } from '@app/config/config.type';
import type { LenderConfiguration } from '@shared/model/lender-config.model';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { LenderService } from '@shared/service/lender/lender.service';
import { StatefulService } from '@app/service/stateful.service';
import { StorageService } from '@workflow/service/storage/storage.service';
import { StorageInfoService } from '@workflow/service/storage-info/storage-info.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { FIELD_ITERATION_SYMBOL, PATH_ITERATION_SYMBOL, PATH_PAGE_ITERATION_SYMBOL, StatefulValue } from '@workflow/class/stateful-value.class';
import { dependencies } from '@workflow/class/workflow.dependency';
import pkg from '../../../../package.json';
import { DevtoolsStateRegistryService, type DevtoolsServiceSnapshot } from './devtools-state-registry.service';

const DEVTOOLS_STORAGE_KEY = 'arcdx.devtools';
const DEVTOOLS_QUERY_PARAM = 'arcdxDevtools';
const DEVTOOLS_BRIDGE_VERSION = '5';
const DEVTOOLS_FEATURE_OVERRIDES_KEY = 'arcdx.devtools.featureOverrides';
const DEVTOOLS_PERMISSION_OVERRIDES_KEY = 'arcdx.devtools.permissionOverrides';
const DEVTOOLS_PERMISSION_SIM_KEY = 'arcdx.devtools.permissionSimEnabled';
const DEVTOOLS_FEATURE_OVERRIDES_EVENT = 'arcdx-devtools:feature-overrides';
const DEVTOOLS_PERMISSION_OVERRIDES_EVENT = 'arcdx-devtools:permission-overrides';
const MAX_SAWDUST_LOGS = 1000;
const MAX_WORKFLOW_REQUESTS = 150;
const MAX_STATEFUL_WRITE_TRACE = 400;
const DEVTOOLS_BRIDGE_SCHEMA_VERSION = '2026.02.11';

type JsonRecord = Record<string, unknown>;

interface DevtoolsRouteSnapshot {
  path: string;
  outlet: string;
  component: string | null;
  params: Record<string, unknown>;
  data: Record<string, unknown>;
  children: DevtoolsRouteSnapshot[];
}

interface DevtoolsSnapshot {
  bridge: {
    version: string;
    enabledAt: string;
  };
  app: {
    name: string;
    version: string;
    environment: string | null;
    instanceName: string | null;
    apiBaseUrl: string | null;
    isDevMode: boolean;
  };
  router: {
    url: string;
    tree: DevtoolsRouteSnapshot | null;
  };
  user: {
    isAuthenticated: boolean;
    permissionCount: number;
    permissionActionCount: number;
    permissions: Record<string, string[]>;
    permissionSimulationEnabled: boolean;
    permissionOverrides: Record<string, string[]>;
  };
  session: DevtoolsSessionInfo;
  lender: {
    active: LenderSummary | null;
    parent: LenderSummary | null;
    childCount: number;
    relatedCount: number;
    switchInProgress: boolean;
  };
  features: {
    combined: Record<string, boolean>;
    toggles: Record<string, boolean>;
    combinedLoaded: boolean;
    togglesLoaded: boolean;
    overrides: Record<string, boolean>;
    overridesEnabled: boolean;
  };
  sawdust: DevtoolsSawdustSummary;
  stateful: DevtoolsStatefulSummary;
  workflow: DevtoolsWorkflowSummary;
  config: DevtoolsConfig | null;
  stateServices: DevtoolsServiceSnapshot[];
}

interface LenderSummary {
  lenderCode?: string;
  lenderName?: string;
  consumerLoanCode?: string;
}

interface DevtoolsConfig {
  tenantId?: string;
  isProd?: boolean;
  arcDXTokenStorageType?: 'localStorage' | 'sessionStorage';
  adminPortalTokenStorageType?: 'localStorage' | 'sessionStorage';
  adminPortalSessionTimeoutInSeconds?: string;
  versionCheckMs?: string;
  dataDog?: {
    env?: string;
    service?: string;
    site?: string;
    version?: string;
  };
  consumerPortalApi?: {
    domain?: string;
    apiBaseURL?: string;
    routeScope?: string;
    version?: string;
    api?: string;
    suffix?: string;
  };
  oid?: {
    apiBaseURL?: string;
    oidConfigName?: string;
    adminPortalOidConfigName?: string;
  };
}

interface ArcDXDevtoolsHook {
  version: string;
  getSnapshot: () => DevtoolsSnapshot;
  getSnapshotLite: () => DevtoolsSnapshot;
  getStatefulSummary: () => DevtoolsStatefulSummary;
  getWorkflowSummary: () => DevtoolsWorkflowSummary;
  getStateServices: () => DevtoolsServiceSnapshot[];
  getCaptureSettings: () => DevtoolsCaptureSettings;
  setStatefulTracing: (enabled: boolean) => boolean;
  setWorkflowCorrelation: (enabled: boolean) => boolean;
  setWorkflowPayloadCapture: (enabled: boolean) => boolean;
  getStatefulDrilldown: (query?: string, limit?: number) => DevtoolsStatefulDrilldown;
  getStatefulDataValue: (key: string) => DevtoolsStatefulValueLookup;
  getStatefulLineageGraph: (key: string, limit?: number) => DevtoolsStatefulLineageGraph;
  getStatefulWriteTrace: (limit?: number, key?: string) => DevtoolsStatefulWriteTrace[];
  getSawdustState: () => DevtoolsSawdustSummary;
  getSawdustLogs: (limit?: number) => DevtoolsSawdustLogEntry[];
  clearSawdustLogs: () => void;
  setSawdustLogLevel: (level: number) => boolean;
  getWorkflowRequests: (limit?: number) => DevtoolsWorkflowRequest[];
  clearWorkflowRequests: () => void;
  simulateWorkflowPath: (flowId: string, mockValues?: Record<string, unknown>) => DevtoolsWorkflowSimulation;
  getWorkflowDriftReport: (limit?: number) => DevtoolsWorkflowDriftReport;
  getHealthChecks: () => DevtoolsHealthReport;
  getBridgeContract: () => DevtoolsBridgeContract;
  setFeatureFlagOverride: (flag: string, value: boolean) => void;
  clearFeatureFlagOverride: (flag: string) => void;
  clearFeatureFlagOverrides: () => void;
  setPermissionSimulation: (enabled: boolean, permissions?: Record<string, string[]>) => void;
  clearPermissionSimulation: () => void;
}

interface DevtoolsSessionInfo {
  expiresAt: string | null;
  expiresInMs: number | null;
  expiresInSeconds: number | null;
  timeoutDurationMs: number | null;
  timeoutDurationSeconds: number | null;
  isExpired: boolean;
}

interface DevtoolsSawdustLogEntry {
  id: string;
  level: string;
  timestamp: string;
  name?: string;
  message?: string;
  context?: unknown;
  metadata?: unknown;
  error?: unknown;
}

interface DevtoolsSawdustSummary {
  logLevel: number;
  logLevelName: string;
  totalLogs: number;
  levelCounts: Record<string, number>;
}

interface DevtoolsStatefulBackendSummary {
  protocol: string;
  type: string;
  initialized: boolean;
  isLoading: boolean;
  dataKeys: number;
  arrayRoots: number;
}

interface DevtoolsStatefulChangedEntry {
  id: string;
  value: unknown;
  savedValue: unknown;
  isLoading: boolean;
  saveLock: boolean;
}

interface DevtoolsStatefulSummary {
  instanceCount: number;
  changedCount: number;
  loadingCount: number;
  saveLockedCount: number;
  backendCount: number;
  backends: DevtoolsStatefulBackendSummary[];
  dataKeyCount: number;
  arrayRootCount: number;
  protocolKeyCounts: Record<string, number>;
  arraySamples: Array<{ path: string; size: number }>;
  dataKeySamples: string[];
  instanceSamples: string[];
  changedSamples: DevtoolsStatefulChangedEntry[];
}

interface DevtoolsStatefulLineage {
  protocol: string | null;
  storageId: string;
  normalizedStorageId: string | null;
  iterationId: number | null;
  pageIterationId: number | null;
  arrayRootPath: string | null;
  backendType: string | null;
}

interface DevtoolsStatefulDrilldownMatch {
  source: 'instance' | 'data' | 'array' | 'backend';
  key: string;
  lineage: DevtoolsStatefulLineage;
  value?: unknown;
  savedValue?: unknown;
  dataValue?: unknown;
  arraySize?: number;
  isChanged?: boolean;
  isLoading?: boolean;
  saveLock?: boolean;
  initialized?: boolean;
}

interface DevtoolsStatefulDrilldown {
  query: string;
  totalMatches: number;
  matches: DevtoolsStatefulDrilldownMatch[];
}

interface DevtoolsStatefulValueLookup {
  key: string;
  found: boolean;
  source: 'instance' | 'data' | 'array' | 'backend' | null;
  lineage: DevtoolsStatefulLineage | null;
  value?: unknown;
  savedValue?: unknown;
  dataValue?: unknown;
  arraySize?: number;
  isChanged?: boolean;
  isLoading?: boolean;
  saveLock?: boolean;
  backend?: {
    protocol: string;
    type: string;
    initialized: boolean;
    isLoading: boolean;
  };
}

interface DevtoolsStatefulBindingLocation {
  flowId: string;
  flowPath: string | null;
  pageId: string;
  pagePath: string | null;
  cardId: string;
  fieldId: string;
}

interface DevtoolsStatefulFieldBinding {
  fieldId: string;
  storageId: string;
  normalizedStorageId: string | null;
  protocol: string | null;
  backendType: string | null;
  locationCount: number;
  locations: DevtoolsStatefulBindingLocation[];
}

interface DevtoolsStatefulLineageGraph {
  key: string;
  lookup: DevtoolsStatefulValueLookup;
  lineage: DevtoolsStatefulLineage | null;
  totalBindings: number;
  bindings: DevtoolsStatefulFieldBinding[];
}

interface DevtoolsStatefulWriteTrace {
  id: string;
  timestamp: string;
  key: string | null;
  actor: string;
  action: string;
  oldValue?: unknown;
  newValue?: unknown;
  savedBefore?: unknown;
  savedAfter?: unknown;
  callSite?: string | null;
  meta?: Record<string, unknown>;
}

interface DevtoolsWorkflowIssue {
  severity: 'error' | 'warn';
  code: string;
  message: string;
  ref?: string;
}

interface DevtoolsWorkflowFlowSummary {
  id: string;
  path: string;
  pageCount: number;
  firstPage: string | null;
  lastPage: string | null;
  runtimeVisiblePageCount: number | null;
  runtimeCompleted: boolean | null;
}

interface DevtoolsWorkflowPayloadSummary {
  topLevelKeys: number;
  productKeys: string[];
  flowCount: number;
  pageCount: number;
  cardCount: number;
  fieldCount: number;
  storageProtocols: string[];
}

interface DevtoolsWorkflowRequest {
  id: string;
  timestamp: string;
  transport: 'fetch' | 'xhr';
  method: string;
  url: string;
  status: number;
  durationMs: number | null;
  ok: boolean;
  error?: string;
  payloadSummary?: DevtoolsWorkflowPayloadSummary | null;
  stateCorrelation?: DevtoolsWorkflowStateCorrelation;
}

interface DevtoolsWorkflowStateCorrelation {
  correlatedAt: string;
  totalChanged: number;
  changedKeys: string[];
  writeCount: number;
  writeKeys: string[];
}

interface DevtoolsWorkflowSummary {
  currentFlowId: string | null;
  currentFlowPath: string | null;
  currentPageId: string | null;
  currentPagePath: string | null;
  routePrefix: string | null;
  flowCount: number;
  pageCount: number;
  cardCount: number;
  fieldCount: number;
  flowSummaries: DevtoolsWorkflowFlowSummary[];
  validation: {
    checkedAt: string;
    issueCount: number;
    issues: DevtoolsWorkflowIssue[];
  };
  graph: DevtoolsWorkflowGraph;
  requests: DevtoolsWorkflowRequest[];
}

interface DevtoolsWorkflowSimulationPage {
  id: string;
  path: string | null;
  index: number;
  cardCount: number;
  fieldCount: number;
  isCurrent: boolean;
}

interface DevtoolsWorkflowSimulation {
  flowId: string;
  valid: boolean;
  flowPath: string | null;
  routePrefix: string | null;
  currentPageId: string | null;
  nextPageId: string | null;
  pageCount: number;
  mockedFieldCount: number;
  missingMockFieldCount: number;
  pages: DevtoolsWorkflowSimulationPage[];
  ruleOutcomes: DevtoolsWorkflowIssue[];
}

interface DevtoolsWorkflowDriftReport {
  checkedAt: string;
  issueCount: number;
  issues: DevtoolsWorkflowIssue[];
}

interface DevtoolsHealthCheck {
  id: string;
  status: 'pass' | 'warn' | 'fail';
  message: string;
  details?: string;
}

interface DevtoolsHealthReport {
  checkedAt: string;
  summary: {
    pass: number;
    warn: number;
    fail: number;
  };
  checks: DevtoolsHealthCheck[];
}

interface DevtoolsBridgeContract {
  schemaVersion: string;
  bridgeVersion: string;
  methods: string[];
  snapshotKeys: string[];
  workflowRequestKeys: string[];
  statefulWriteTraceKeys: string[];
}

interface DevtoolsCaptureSettings {
  statefulTracingEnabled: boolean;
  workflowCorrelationEnabled: boolean;
  workflowPayloadCaptureEnabled: boolean;
}

interface DevtoolsWorkflowGraphFlow {
  id: string;
  path: string;
  pageIds: string[];
  missingPageIds: string[];
}

interface DevtoolsWorkflowGraphPage {
  id: string;
  path: string | null;
  type: string;
  cardIds: string[];
  missingCardIds: string[];
}

interface DevtoolsWorkflowGraphCard {
  id: string;
  fieldIds: string[];
  missingFieldIds: string[];
}

interface DevtoolsWorkflowGraph {
  flows: DevtoolsWorkflowGraphFlow[];
  pagesById: Record<string, DevtoolsWorkflowGraphPage>;
  cardsById: Record<string, DevtoolsWorkflowGraphCard>;
  unreachablePageIds: string[];
  unreachableCardIds: string[];
  unreachableFieldIds: string[];
}

@Injectable({
  providedIn: 'root'
})
export class DevtoolsBridgeService {
  readonly #platformId = inject(PLATFORM_ID);
  readonly #router = inject(Router);
  readonly #tokenService = inject(TokenService);
  readonly #featureFlagService = inject(FeatureFlagService);
  readonly #runtimeConfigService = inject(RuntimeConfigService);
  readonly #userService = inject(UserService);
  readonly #lenderService = inject(LenderService);
  readonly #sawdust = inject(SawDust);
  readonly #statefulService = inject(StatefulService);
  readonly #storageService = inject(StorageService);
  readonly #storageInfoService = inject(StorageInfoService);
  readonly #workflowService = inject(WorkflowService);
  readonly #stateRegistry = inject(DevtoolsStateRegistryService);

  readonly #registered = signal(false);
  readonly #captureInstalled = signal(false);
  readonly #sawdustSubscriptionRegistered = signal(false);
  readonly #statefulWriteTraceInstalled = signal(false);
  readonly #statefulTracingEnabled = signal(false);
  readonly #workflowCorrelationEnabled = signal(false);
  readonly #workflowPayloadCaptureEnabled = signal(false);

  readonly #sawdustLogs: DevtoolsSawdustLogEntry[] = [];
  readonly #workflowRequests: DevtoolsWorkflowRequest[] = [];
  readonly #statefulWriteTrace: DevtoolsStatefulWriteTrace[] = [];
  #uniqueIdCounter = 0;

  init(): void {
    if (!isPlatformBrowser(this.#platformId)) return;
    if (this.#registered()) return;
    if (!this.#isEnabled()) return;

    this.#attachSawdustListener();
    this.#installWorkflowRequestCapture();
    this.#installStatefulWriteTraceCapture();

    const hook: ArcDXDevtoolsHook = {
      version: DEVTOOLS_BRIDGE_VERSION,
      getSnapshot: () => this.#buildSnapshot(),
      getSnapshotLite: () => this.#buildSnapshotLite(),
      getStatefulSummary: () => this.#buildStatefulSummary(),
      getWorkflowSummary: () => this.#buildWorkflowSummary(),
      getStateServices: () => this.#stateRegistry.snapshot(),
      getCaptureSettings: () => this.#getCaptureSettings(),
      setStatefulTracing: (enabled) => this.#setStatefulTracing(enabled),
      setWorkflowCorrelation: (enabled) => this.#setWorkflowCorrelation(enabled),
      setWorkflowPayloadCapture: (enabled) => this.#setWorkflowPayloadCapture(enabled),
      getStatefulDrilldown: (query, limit) => this.#getStatefulDrilldown(query, limit),
      getStatefulDataValue: (key) => this.#getStatefulDataValue(key),
      getStatefulLineageGraph: (key, limit) => this.#getStatefulLineageGraph(key, limit),
      getStatefulWriteTrace: (limit, key) => this.#getStatefulWriteTrace(limit, key),
      getSawdustState: () => this.#buildSawdustSummary(),
      getSawdustLogs: (limit) => this.#getSawdustLogs(limit),
      clearSawdustLogs: () => this.#clearSawdustLogs(),
      setSawdustLogLevel: (level) => this.#setSawdustLogLevel(level),
      getWorkflowRequests: (limit) => this.#getWorkflowRequests(limit),
      clearWorkflowRequests: () => this.#clearWorkflowRequests(),
      simulateWorkflowPath: (flowId, mockValues) => this.#simulateWorkflowPath(flowId, mockValues),
      getWorkflowDriftReport: (limit) => this.#getWorkflowDriftReport(limit),
      getHealthChecks: () => this.#getHealthChecks(),
      getBridgeContract: () => this.#getBridgeContract(),
      setFeatureFlagOverride: (flag, value) => this.#setFeatureOverride(flag, value),
      clearFeatureFlagOverride: (flag) => this.#clearFeatureOverride(flag),
      clearFeatureFlagOverrides: () => this.#clearFeatureOverrides(),
      setPermissionSimulation: (enabled, permissions) => this.#setPermissionSimulation(enabled, permissions),
      clearPermissionSimulation: () => this.#clearPermissionSimulation()
    };

    const windowRef = window as Window & { __ARCDX_DEVTOOLS__?: ArcDXDevtoolsHook };
    if (!windowRef.__ARCDX_DEVTOOLS__) {
      Object.defineProperty(windowRef, '__ARCDX_DEVTOOLS__', {
        value: hook,
        configurable: true,
        writable: false
      });
      windowRef.dispatchEvent(new CustomEvent('arcdx-devtools:ready'));
    }

    this.#registered.set(true);
  }

  #attachSawdustListener(): void {
    if (this.#sawdustSubscriptionRegistered()) return;

    this.#sawdust.logEventEmitter.subscribe((event) => {
      this.#sawdustLogs.unshift(this.#toSawdustLogEntry(event));
      if (this.#sawdustLogs.length > MAX_SAWDUST_LOGS) {
        this.#sawdustLogs.length = MAX_SAWDUST_LOGS;
      }
    });

    this.#sawdustSubscriptionRegistered.set(true);
  }

  #installStatefulWriteTraceCapture(): void {
    if (this.#statefulWriteTraceInstalled()) return;

    const windowRef = window as Window & { __ARCDX_DEVTOOLS_STATEFUL_TRACE_PATCHED__?: boolean };
    if (windowRef.__ARCDX_DEVTOOLS_STATEFUL_TRACE_PATCHED__) {
      this.#statefulWriteTraceInstalled.set(true);
      return;
    }
    windowRef.__ARCDX_DEVTOOLS_STATEFUL_TRACE_PATCHED__ = true;
    const isStatefulTraceEnabled = () => this.#statefulTracingEnabled();

    const record = (
      actor: string,
      action: string,
      key: string | null,
      oldValue: unknown,
      newValue: unknown,
      savedBefore?: unknown,
      savedAfter?: unknown,
      meta?: Record<string, unknown>
    ) => {
      this.#recordStatefulWriteTrace({
        actor,
        action,
        key,
        oldValue,
        newValue,
        savedBefore,
        savedAfter,
        meta
      });
    };

    const patchServiceMethod = (methodName: string): void => {
      const service = this.#statefulService as unknown as Record<string, unknown>;
      const original = service[methodName];
      if (typeof original !== 'function') return;
      const getStatefulDataValue = (key: string) => this.#getStatefulDataValue(key);
      service[methodName] = function patchedStatefulServiceMethod(this: Record<string, unknown>, ...args: unknown[]) {
        if (!isStatefulTraceEnabled()) {
          return (original as (...innerArgs: unknown[]) => unknown).apply(this, args);
        }
        const keyArg = args[0];
        const key = typeof keyArg === 'string' ? keyArg : null;
        let beforeValue: unknown = null;
        let beforeSaved: unknown = null;

        if (key) {
          const lookup = getStatefulDataValue(key);
          beforeValue = lookup.value ?? lookup.dataValue ?? lookup.arraySize ?? null;
          beforeSaved = lookup.savedValue ?? null;
        }

        const result = (original as (...innerArgs: unknown[]) => unknown).apply(this, args);

        if (methodName === 'wrap') {
          const wrapped = result as Record<string, unknown> | null;
          const wrappedKey = typeof wrapped?.id === 'string' ? wrapped.id : key;
          record(`StatefulService.${methodName}`, 'wrap', wrappedKey || null, null, null, null, null, {
            iterationId: args[1] ?? null,
            pageIterationId: args[2] ?? null
          });
        } else {
          const lookupAfter = key ? getStatefulDataValue(key) : null;
          const afterValue = lookupAfter?.value ?? lookupAfter?.dataValue ?? lookupAfter?.arraySize ?? null;
          const afterSaved = lookupAfter?.savedValue ?? null;

          record(`StatefulService.${methodName}`, methodName, key, beforeValue, afterValue, beforeSaved, afterSaved, {
            argCount: args.length
          });
        }
        return result;
      };
    };

    ['wrap', 'arrayAdd', 'arrayRemove', 'arrayTruncate', 'arrayTruncateWithPageIteration', 'reloadData', 'clear', 'saveChangedValues'].forEach(
      (methodName) => patchServiceMethod(methodName)
    );

    const valuePrototype = StatefulValue.prototype as unknown as Record<string, unknown>;

    const valueDescriptor = Object.getOwnPropertyDescriptor(valuePrototype, 'value');
    if (valueDescriptor?.set && valueDescriptor.get) {
      const originalSet = valueDescriptor.set;
      const originalGet = valueDescriptor.get;
      Object.defineProperty(valuePrototype, 'value', {
        configurable: true,
        enumerable: valueDescriptor.enumerable ?? false,
        get: originalGet,
        set(this: { id?: string; savedValue?: unknown }, nextValue: unknown) {
          if (!isStatefulTraceEnabled()) {
            originalSet.call(this, nextValue);
            return;
          }
          const key = typeof this.id === 'string' ? this.id : null;
          const beforeValue = originalGet.call(this);
          const beforeSaved = this.savedValue;
          originalSet.call(this, nextValue);
          const afterValue = originalGet.call(this);
          const afterSaved = this.savedValue;
          record('StatefulValue.value', 'set', key, beforeValue, afterValue, beforeSaved, afterSaved);
        }
      });
    }

    const patchValueMethod = (methodName: 'setSavedValue' | 'revert' | 'save' | 'saveLock' | 'saveUnlock') => {
      const original = valuePrototype[methodName];
      if (typeof original !== 'function') return;
      const sanitizeValue = (value: unknown) => this.#sanitizeValue(value);

      valuePrototype[methodName] = function patchedStatefulValueMethod(this: { id?: string; value?: unknown; savedValue?: unknown }, ...args: unknown[]) {
        if (!isStatefulTraceEnabled()) {
          return (original as (...innerArgs: unknown[]) => unknown).apply(this, args);
        }
        const key = typeof this.id === 'string' ? this.id : null;
        const beforeValue = this.value;
        const beforeSaved = this.savedValue;
        const result = (original as (...innerArgs: unknown[]) => unknown).apply(this, args);

        if (result instanceof Promise) {
          return result.then((resolved) => {
            const afterValue = this.value;
            const afterSaved = this.savedValue;
            record(`StatefulValue.${methodName}`, methodName, key, beforeValue, afterValue, beforeSaved, afterSaved, {
              resolved: sanitizeValue(resolved)
            });
            return resolved;
          });
        }

        const afterValue = this.value;
        const afterSaved = this.savedValue;
        record(`StatefulValue.${methodName}`, methodName, key, beforeValue, afterValue, beforeSaved, afterSaved);
        return result;
      };
    };

    ['setSavedValue', 'revert', 'save', 'saveLock', 'saveUnlock'].forEach((methodName) => {
      patchValueMethod(methodName as 'setSavedValue' | 'revert' | 'save' | 'saveLock' | 'saveUnlock');
    });

    this.#statefulWriteTraceInstalled.set(true);
  }

  #installWorkflowRequestCapture(): void {
    if (this.#captureInstalled()) return;

    const windowRef = window as Window & { __ARCDX_DEVTOOLS_CAPTURE__?: boolean };
    if (windowRef.__ARCDX_DEVTOOLS_CAPTURE__) {
      this.#captureInstalled.set(true);
      return;
    }

    windowRef.__ARCDX_DEVTOOLS_CAPTURE__ = true;

    const originalFetch = window.fetch.bind(window);
    const shouldTrackWorkflowRequest = (url: string) => this.#shouldTrackWorkflowRequest(url);
    const resolveRequestUrl = (input: RequestInfo | URL) => this.#resolveRequestUrl(input);
    const recordWorkflowRequest = (entry: DevtoolsWorkflowRequest) => this.#recordWorkflowRequest(entry);
    const updateWorkflowRequest = (id: string, patch: Partial<DevtoolsWorkflowRequest>) => this.#updateWorkflowRequest(id, patch);
    const captureWorkflowPayloadFromResponse = (id: string, response: Response) =>
      this.#captureWorkflowPayloadFromResponse(id, response);
    const summarizeWorkflowPayload = (payload: unknown) => this.#summarizeWorkflowPayload(payload);
    const toErrorString = (error: unknown) => this.#toErrorString(error);
    const uniqueId = (prefix: string) => this.#uniqueId(prefix);
    const asRecord = (value: unknown) => this.#asRecord(value);
    const isWorkflowCorrelationEnabled = () => this.#workflowCorrelationEnabled();
    const isWorkflowPayloadCaptureEnabled = () => this.#workflowPayloadCaptureEnabled();
    const captureStatefulSignatures = () => this.#captureStatefulInstanceSignatures();
    const correlateWorkflowRequest = (id: string, beforeSignatures: Record<string, string>, startedAtIso: string) =>
      this.#correlateWorkflowRequestState(id, beforeSignatures, startedAtIso);

    window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const method = (init?.method || 'GET').toUpperCase();
      const url = resolveRequestUrl(input);
      const shouldTrack = shouldTrackWorkflowRequest(url);

      if (!shouldTrack) {
        return originalFetch(input, init);
      }

      const startedAt = new Date().toISOString();
      const startTime = performance.now();
      const beforeSignatures = isWorkflowCorrelationEnabled() ? captureStatefulSignatures() : null;

      try {
        const response = await originalFetch(input, init);
        const durationMs = Math.round(performance.now() - startTime);

        const requestId = recordWorkflowRequest({
          id: uniqueId('wf-fetch'),
          timestamp: startedAt,
          transport: 'fetch',
          method,
          url,
          status: response.status,
          durationMs,
          ok: response.ok
        });

        if (isWorkflowPayloadCaptureEnabled()) {
          captureWorkflowPayloadFromResponse(requestId, response.clone());
        }
        if (beforeSignatures) {
          correlateWorkflowRequest(requestId, beforeSignatures, startedAt);
          window.setTimeout(() => correlateWorkflowRequest(requestId, beforeSignatures, startedAt), 120);
        }
        return response;
      } catch (error) {
        const requestId = recordWorkflowRequest({
          id: uniqueId('wf-fetch'),
          timestamp: startedAt,
          transport: 'fetch',
          method,
          url,
          status: 0,
          durationMs: Math.round(performance.now() - startTime),
          ok: false,
          error: toErrorString(error)
        });
        if (beforeSignatures) {
          correlateWorkflowRequest(requestId, beforeSignatures, startedAt);
        }
        throw error;
      }
    };

    const xhrProto = XMLHttpRequest.prototype as XMLHttpRequest & {
      open: (...args: unknown[]) => unknown;
      send: (...args: unknown[]) => unknown;
    };

    const originalOpen = xhrProto.open;
    const originalSend = xhrProto.send;

    xhrProto.open = function patchedOpen(this: XMLHttpRequest & { __arcdxMeta?: JsonRecord }, ...args: unknown[]) {
      const method = String(args[0] ?? 'GET').toUpperCase();
      const url = args[1] ? String(args[1]) : '';
      this.__arcdxMeta = {
        method,
        url,
        startedAt: new Date().toISOString(),
        startTime: performance.now(),
        shouldTrack: shouldTrackWorkflowRequest(url),
        beforeSignatures: isWorkflowCorrelationEnabled() ? captureStatefulSignatures() : null
      };
      return originalOpen.apply(this, args as never[]);
    };

    xhrProto.send = function patchedSend(this: XMLHttpRequest & { __arcdxMeta?: JsonRecord }, ...args: unknown[]) {
      const meta = asRecord(this.__arcdxMeta);
      const shouldTrack = meta?.shouldTrack === true;

      if (shouldTrack) {
        this.addEventListener('loadend', () => {
          const durationMs = typeof meta?.startTime === 'number' ? Math.round(performance.now() - meta.startTime) : null;
          const requestId = recordWorkflowRequest({
            id: uniqueId('wf-xhr'),
            timestamp: String(meta?.startedAt || new Date().toISOString()),
            transport: 'xhr',
            method: String(meta?.method || 'GET'),
            url: String(meta?.url || ''),
            status: this.status || 0,
            durationMs,
            ok: this.status >= 200 && this.status < 400
          });

          const beforeSignaturesRecord = asRecord(meta?.beforeSignatures) || {};
          const beforeSignatures: Record<string, string> = Object.entries(beforeSignaturesRecord).reduce<Record<string, string>>(
            (acc, [key, value]) => {
              acc[key] = String(value);
              return acc;
            },
            {}
          );
          if (Object.keys(beforeSignatures).length) {
            const startedAt = String(meta?.startedAt || new Date().toISOString());
            correlateWorkflowRequest(requestId, beforeSignatures, startedAt);
            window.setTimeout(() => correlateWorkflowRequest(requestId, beforeSignatures, startedAt), 120);
          }

          if (isWorkflowPayloadCaptureEnabled() && typeof this.responseText === 'string' && this.responseText.trim()) {
            try {
              const parsed = JSON.parse(this.responseText);
              const summary = summarizeWorkflowPayload(parsed);
              if (summary) {
                updateWorkflowRequest(requestId, { payloadSummary: summary });
              }
            } catch {
              // no-op (response is not json)
            }
          }
        });
      }

      return originalSend.apply(this, args as never[]);
    };

    this.#captureInstalled.set(true);
  }

  #captureWorkflowPayloadFromResponse(requestId: string, response: Response): void {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.toLowerCase().includes('json')) return;

    response
      .json()
      .then((payload) => {
        const summary = this.#summarizeWorkflowPayload(payload);
        if (summary) {
          this.#updateWorkflowRequest(requestId, { payloadSummary: summary });
        }
      })
      .catch(() => {
        // ignore payload parsing errors
      });
  }

  #recordWorkflowRequest(entry: DevtoolsWorkflowRequest): string {
    this.#workflowRequests.unshift(entry);
    if (this.#workflowRequests.length > MAX_WORKFLOW_REQUESTS) {
      this.#workflowRequests.length = MAX_WORKFLOW_REQUESTS;
    }
    return entry.id;
  }

  #updateWorkflowRequest(id: string, patch: Partial<DevtoolsWorkflowRequest>): void {
    const index = this.#workflowRequests.findIndex((request) => request.id === id);
    if (index < 0) return;

    this.#workflowRequests[index] = {
      ...this.#workflowRequests[index],
      ...patch
    };
  }

  #shouldTrackWorkflowRequest(url: string): boolean {
    const normalized = (url || '').toLowerCase();
    return (
      normalized.includes('/workflow/config') ||
      normalized.includes('workflow-pages') ||
      normalized.includes('workflow-cards') ||
      normalized.includes('workflow-fields') ||
      normalized.includes('storage-info') ||
      (normalized.includes('config') && normalized.includes('products='))
    );
  }

  #resolveRequestUrl(input: RequestInfo | URL): string {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.toString();
    if (input instanceof Request) return input.url;
    return String(input);
  }

  #summarizeWorkflowPayload(payload: unknown): DevtoolsWorkflowPayloadSummary | null {
    const root = this.#asRecord(payload);
    if (!root) return null;

    const topLevelKeys = Object.keys(root);

    const products: Array<{ key: string; config: JsonRecord }> = [];

    const rootLooksLikeWorkflowConfig = this.#looksLikeWorkflowConfig(root);
    if (rootLooksLikeWorkflowConfig) {
      products.push({ key: 'root', config: root });
    }

    topLevelKeys.forEach((key) => {
      const candidate = this.#asRecord(root[key]);
      if (!candidate) return;
      if (this.#looksLikeWorkflowConfig(candidate)) {
        products.push({ key, config: candidate });
      }
    });

    if (!products.length) return null;

    const storageProtocols = new Set<string>();
    let flowCount = 0;
    let pageCount = 0;
    let cardCount = 0;
    let fieldCount = 0;

    products.forEach(({ config }) => {
      flowCount += this.#countRecordKeys(config.workflows);
      pageCount += this.#countRecordKeys(config['workflow-pages']);
      cardCount += this.#countRecordKeys(config['workflow-cards']);
      fieldCount += this.#countRecordKeys(config['workflow-fields']);

      const storage = this.#asRecord(config.storage);
      if (storage) {
        Object.keys(storage).forEach((protocol) => storageProtocols.add(protocol));
      }
    });

    return {
      topLevelKeys: topLevelKeys.length,
      productKeys: products.map((item) => item.key),
      flowCount,
      pageCount,
      cardCount,
      fieldCount,
      storageProtocols: Array.from(storageProtocols).sort((a, b) => a.localeCompare(b))
    };
  }

  #looksLikeWorkflowConfig(config: JsonRecord): boolean {
    return Boolean(config.workflows || config['workflow-pages'] || config['workflow-cards'] || config['workflow-fields'] || config.storage);
  }

  #isEnabled(): boolean {
    if (isDevMode()) return true;

    try {
      const queryEnabled = new URLSearchParams(window.location.search).get(DEVTOOLS_QUERY_PARAM) === '1';
      const storageEnabled = window.localStorage.getItem(DEVTOOLS_STORAGE_KEY) === 'true';
      return queryEnabled || storageEnabled;
    } catch {
      return false;
    }
  }

  #isOverrideEnabled(): boolean {
    return this.#isEnabled();
  }

  #readOverride<T>(key: string): T | null {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  #readFeatureOverrides(): Record<string, boolean> {
    const overrides = this.#readOverride<Record<string, boolean>>(DEVTOOLS_FEATURE_OVERRIDES_KEY);
    return overrides && typeof overrides === 'object' ? overrides : {};
  }

  #readPermissionOverrides(): Record<string, string[]> {
    const overrides = this.#readOverride<Record<string, string[]>>(DEVTOOLS_PERMISSION_OVERRIDES_KEY);
    return overrides && typeof overrides === 'object' ? overrides : {};
  }

  #readPermissionSimulationEnabled(): boolean {
    const raw = window.localStorage.getItem(DEVTOOLS_PERMISSION_SIM_KEY);
    return raw === 'true';
  }

  #writeOverride(key: string, value: unknown): void {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(value));
  }

  #dispatchOverrideEvent(eventName: string): void {
    window.dispatchEvent(new CustomEvent(eventName));
  }

  #setFeatureOverride(flag: string, value: boolean): void {
    if (!this.#isOverrideEnabled()) return;
    const overrides = this.#readFeatureOverrides();
    overrides[flag] = value;
    this.#writeOverride(DEVTOOLS_FEATURE_OVERRIDES_KEY, overrides);
    this.#dispatchOverrideEvent(DEVTOOLS_FEATURE_OVERRIDES_EVENT);
  }

  #clearFeatureOverrides(): void {
    if (!this.#isOverrideEnabled()) return;
    window.localStorage.removeItem(DEVTOOLS_FEATURE_OVERRIDES_KEY);
    this.#dispatchOverrideEvent(DEVTOOLS_FEATURE_OVERRIDES_EVENT);
  }

  #clearFeatureOverride(flag: string): void {
    if (!this.#isOverrideEnabled()) return;
    const overrides = this.#readFeatureOverrides();
    if (!(flag in overrides)) return;
    delete overrides[flag];
    this.#writeOverride(DEVTOOLS_FEATURE_OVERRIDES_KEY, overrides);
    this.#dispatchOverrideEvent(DEVTOOLS_FEATURE_OVERRIDES_EVENT);
  }

  #setPermissionSimulation(enabled: boolean, permissions?: Record<string, string[]>): void {
    if (!this.#isOverrideEnabled()) return;
    window.localStorage.setItem(DEVTOOLS_PERMISSION_SIM_KEY, enabled ? 'true' : 'false');
    if (permissions) {
      this.#writeOverride(DEVTOOLS_PERMISSION_OVERRIDES_KEY, permissions);
    }
    this.#dispatchOverrideEvent(DEVTOOLS_PERMISSION_OVERRIDES_EVENT);
  }

  #clearPermissionSimulation(): void {
    if (!this.#isOverrideEnabled()) return;
    window.localStorage.removeItem(DEVTOOLS_PERMISSION_OVERRIDES_KEY);
    window.localStorage.removeItem(DEVTOOLS_PERMISSION_SIM_KEY);
    this.#dispatchOverrideEvent(DEVTOOLS_PERMISSION_OVERRIDES_EVENT);
  }

  #buildSnapshotBase(): Omit<DevtoolsSnapshot, 'stateful' | 'workflow' | 'stateServices'> {
    const config = this.#runtimeConfigService.getConfig ?? null;
    const appConfig = this.#sanitizeConfig(config);
    const overridesEnabled = this.#isOverrideEnabled();
    const featureOverrides = overridesEnabled ? this.#readFeatureOverrides() : {};
    const permissionOverrides = overridesEnabled ? this.#readPermissionOverrides() : {};
    const permissionSimulationEnabled = overridesEnabled ? this.#readPermissionSimulationEnabled() : false;

    const basePermissions = this.#extractPermissionsFromToken();
    const permissions = permissionSimulationEnabled && Object.keys(permissionOverrides).length ? permissionOverrides : basePermissions;
    const permissionEntries = Object.entries(permissions);
    const permissionActionCount = permissionEntries.reduce((total, [, actions]) => total + (Array.isArray(actions) ? actions.length : 0), 0);

    const toggles = this.#readFeatureToggles();
    const combined = { ...toggles };
    if (overridesEnabled) {
      Object.assign(combined, featureOverrides);
    }

    const activeLender = this.#mapLender(this.#lenderService.lenderDataInfo ?? this.#lenderService.lenderData ?? null);
    let environment: string | null = null;
    if (config) {
      environment = config.isProd ? 'production' : 'development';
    }

    return {
      bridge: {
        version: DEVTOOLS_BRIDGE_VERSION,
        enabledAt: new Date().toISOString()
      },
      app: {
        name: config?.consumerPortalApi?.name ?? 'arcDX',
        version: pkg.version ?? '0.0.0',
        environment,
        instanceName: config?.dataDog?.env ?? null,
        apiBaseUrl: this.#resolveApiBaseUrl(config),
        isDevMode: isDevMode()
      },
      router: {
        url: this.#router.url ?? '',
        tree: this.#mapRoute(this.#router.routerState?.snapshot?.root ?? null)
      },
      user: {
        isAuthenticated: this.#userService.isAuthenticated,
        permissionCount: permissionEntries.length,
        permissionActionCount,
        permissions,
        permissionSimulationEnabled,
        permissionOverrides
      },
      session: this.#buildSession(config),
      lender: {
        active: activeLender,
        parent: null,
        childCount: 0,
        relatedCount: 0,
        switchInProgress: false
      },
      features: {
        combined,
        toggles,
        combinedLoaded: Object.keys(combined).length > 0,
        togglesLoaded: Object.keys(toggles).length > 0,
        overrides: featureOverrides,
        overridesEnabled
      },
      sawdust: this.#buildSawdustSummary(),
      config: appConfig
    };
  }

  #buildSnapshot(): DevtoolsSnapshot {
    const base = this.#buildSnapshotBase();
    return {
      ...base,
      stateful: this.#buildStatefulSummary(),
      workflow: this.#buildWorkflowSummary(),
      stateServices: this.#stateRegistry.snapshot()
    };
  }

  #buildSnapshotLite(): DevtoolsSnapshot {
    const base = this.#buildSnapshotBase();
    return {
      ...base,
      stateful: this.#buildEmptyStatefulSummary(),
      workflow: this.#buildEmptyWorkflowSummary(),
      stateServices: []
    };
  }

  #buildEmptyStatefulSummary(): DevtoolsStatefulSummary {
    return {
      instanceCount: 0,
      changedCount: 0,
      loadingCount: 0,
      saveLockedCount: 0,
      backendCount: 0,
      backends: [],
      dataKeyCount: 0,
      arrayRootCount: 0,
      protocolKeyCounts: {},
      arraySamples: [],
      dataKeySamples: [],
      instanceSamples: [],
      changedSamples: []
    };
  }

  #buildEmptyWorkflowSummary(): DevtoolsWorkflowSummary {
    return {
      currentFlowId: null,
      currentFlowPath: null,
      currentPageId: null,
      currentPagePath: null,
      routePrefix: this.#workflowService.routePrefix || null,
      flowCount: 0,
      pageCount: 0,
      cardCount: 0,
      fieldCount: 0,
      flowSummaries: [],
      validation: {
        checkedAt: new Date().toISOString(),
        issueCount: 0,
        issues: []
      },
      graph: {
        flows: [],
        pagesById: {},
        cardsById: {},
        unreachablePageIds: [],
        unreachableCardIds: [],
        unreachableFieldIds: []
      },
      requests: []
    };
  }

  #buildSawdustSummary(): DevtoolsSawdustSummary {
    const levelCounts: Record<string, number> = {};
    this.#sawdustLogs.forEach((entry) => {
      const key = (entry.level || 'unknown').toLowerCase();
      levelCounts[key] = (levelCounts[key] || 0) + 1;
    });

    const level = Number(this.#sawdust.getLogLevel?.() ?? LogLevel.info);
    return {
      logLevel: level,
      logLevelName: this.#logLevelName(level),
      totalLogs: this.#sawdustLogs.length,
      levelCounts
    };
  }

  #getSawdustLogs(limit?: number): DevtoolsSawdustLogEntry[] {
    const max = this.#toFiniteNumber(limit, true) ?? 250;
    const safeMax = Math.max(1, Math.min(1000, max));
    return this.#sawdustLogs.slice(0, safeMax).map((entry) => ({ ...entry }));
  }

  #clearSawdustLogs(): void {
    this.#sawdustLogs.length = 0;
  }

  #setSawdustLogLevel(level: number): boolean {
    const requested = this.#toFiniteNumber(level, true);
    if (requested === null) return false;

    const allowedLevels = [LogLevel.debug, LogLevel.info, LogLevel.warn, LogLevel.error] as number[];
    if (!allowedLevels.includes(requested)) return false;

    this.#sawdust.setLevel(requested as (typeof LogLevel)[keyof typeof LogLevel]);
    return true;
  }

  #buildStatefulSummary(): DevtoolsStatefulSummary {
    const instancesMap = this.#getStatefulInstancesMap();
    const instanceEntries = Object.entries(instancesMap).filter(([, value]) => this.#isRecord(value));

    const data = this.#asRecord(this.#storageService.data) || {};
    const dataKeys = Object.keys(data);

    const arraySizes = this.#asRecord(this.#storageService.arraySizes) || {};
    const arrayEntries = Object.entries(arraySizes)
      .map(([path, size]) => ({ path, size: Number(size) || 0 }))
      .sort((a, b) => b.size - a.size);

    const protocolKeyCounts: Record<string, number> = {};
    dataKeys.forEach((key) => {
      const protocol = key.split(':')[0] || 'unknown';
      protocolKeyCounts[protocol] = (protocolKeyCounts[protocol] || 0) + 1;
    });

    let changedCount = 0;
    let loadingCount = 0;
    let saveLockedCount = 0;

    const changedSamples: DevtoolsStatefulChangedEntry[] = [];

    instanceEntries.forEach(([id, rawInstance]) => {
      const instance = rawInstance as Record<string, unknown>;
      const isChanged = Boolean(this.#safeRead(instance, 'isChanged'));
      const isLoading = Boolean(this.#safeRead(instance, 'isLoading'));
      const saveLock = Boolean(this.#safeRead(instance, '_saveLock'));

      if (isChanged) changedCount += 1;
      if (isLoading) loadingCount += 1;
      if (saveLock) saveLockedCount += 1;

      if (isChanged && changedSamples.length < 40) {
        changedSamples.push({
          id,
          value: this.#sanitizeValue(this.#safeRead(instance, 'value')),
          savedValue: this.#sanitizeValue(this.#safeRead(instance, 'savedValue')),
          isLoading,
          saveLock
        });
      }
    });

    const backendsMap = this.#asRecord(this.#storageService.backends) || {};
    const backendEntries: DevtoolsStatefulBackendSummary[] = Object.entries(backendsMap)
      .map(([protocol, backend]) => {
        const backendRecord = this.#asRecord(backend) || {};
        const arrayRootCount = Object.keys(arraySizes).filter((path) => path.startsWith(`${protocol}:`)).length;

        return {
          protocol,
          type: String(this.#safeRead(backendRecord, 'type') || 'unknown'),
          initialized: Boolean(this.#safeRead(backendRecord, 'initialized')),
          isLoading: Boolean(this.#safeRead(backendRecord, 'isLoading')),
          dataKeys: protocolKeyCounts[protocol] || 0,
          arrayRoots: arrayRootCount
        };
      })
      .sort((a, b) => a.protocol.localeCompare(b.protocol));

    return {
      instanceCount: instanceEntries.length,
      changedCount,
      loadingCount,
      saveLockedCount,
      backendCount: backendEntries.length,
      backends: backendEntries,
      dataKeyCount: dataKeys.length,
      arrayRootCount: Object.keys(arraySizes).length,
      protocolKeyCounts,
      arraySamples: arrayEntries.slice(0, 40),
      dataKeySamples: dataKeys.slice(0, 60),
      instanceSamples: instanceEntries.map(([id]) => id).slice(0, 60),
      changedSamples
    };
  }

  #getStatefulInstancesMap(): Record<string, unknown> {
    return this.#asRecord((this.#statefulService as unknown as Record<string, unknown>).instances) || {};
  }

  #buildStatefulLineage(storageId: string): DevtoolsStatefulLineage {
    const protocol = storageId.includes(':') ? storageId.split(':')[0] : null;
    const backendRecord = protocol ? this.#asRecord(this.#storageService.backends?.[protocol]) : null;
    const backendType = backendRecord ? String(this.#safeRead(backendRecord, 'type') || 'unknown') : null;

    let normalizedStorageId: string | null = null;
    let iterationId: number | null = null;
    let pageIterationId: number | null = null;

    try {
      const parsed = this.#storageInfoService.parse(storageId);
      normalizedStorageId = parsed?.storageId || null;
      iterationId = typeof parsed?.iterationId === 'number' ? parsed.iterationId : null;
      pageIterationId = typeof parsed?.pageIterationId === 'number' ? parsed.pageIterationId : null;
    } catch {
      normalizedStorageId = null;
    }

    let arrayRootPath: string | null = null;
    try {
      if (storageId.includes(PATH_ITERATION_SYMBOL) || storageId.includes(PATH_PAGE_ITERATION_SYMBOL)) {
        arrayRootPath = this.#storageService.arrayRootPathOf(storageId, pageIterationId ?? undefined);
      } else if (normalizedStorageId && normalizedStorageId.includes(PATH_ITERATION_SYMBOL)) {
        arrayRootPath = this.#storageService.arrayRootPathOf(normalizedStorageId, pageIterationId ?? undefined);
      }
    } catch {
      arrayRootPath = null;
    }

    return {
      protocol,
      storageId,
      normalizedStorageId,
      iterationId,
      pageIterationId,
      arrayRootPath,
      backendType
    };
  }

  #getStatefulDataValue(key: string): DevtoolsStatefulValueLookup {
    const safeKey = String(key || '').trim();
    if (!safeKey) {
      return {
        key: safeKey,
        found: false,
        source: null,
        lineage: null
      };
    }

    const instancesMap = this.#getStatefulInstancesMap();
    const data = this.#asRecord(this.#storageService.data) || {};
    const arraySizes = this.#asRecord(this.#storageService.arraySizes) || {};
    const backends = this.#asRecord(this.#storageService.backends) || {};

    if (this.#isRecord(instancesMap[safeKey])) {
      const instance = instancesMap[safeKey];
      return {
        key: safeKey,
        found: true,
        source: 'instance',
        lineage: this.#buildStatefulLineage(safeKey),
        value: this.#sanitizeValue(this.#safeRead(instance, 'value')),
        savedValue: this.#sanitizeValue(this.#safeRead(instance, 'savedValue')),
        isChanged: Boolean(this.#safeRead(instance, 'isChanged')),
        isLoading: Boolean(this.#safeRead(instance, 'isLoading')),
        saveLock: Boolean(this.#safeRead(instance, '_saveLock'))
      };
    }

    if (Object.prototype.hasOwnProperty.call(data, safeKey)) {
      return {
        key: safeKey,
        found: true,
        source: 'data',
        lineage: this.#buildStatefulLineage(safeKey),
        dataValue: this.#sanitizeValue(data[safeKey])
      };
    }

    if (Object.prototype.hasOwnProperty.call(arraySizes, safeKey)) {
      const protocol = safeKey.includes(':') ? safeKey.split(':')[0] : null;
      const backendRecord = protocol ? this.#asRecord(backends[protocol]) : null;
      return {
        key: safeKey,
        found: true,
        source: 'array',
        lineage: {
          protocol,
          storageId: safeKey,
          normalizedStorageId: null,
          iterationId: null,
          pageIterationId: null,
          arrayRootPath: safeKey,
          backendType: backendRecord ? String(this.#safeRead(backendRecord, 'type') || 'unknown') : null
        },
        arraySize: Number(arraySizes[safeKey]) || 0
      };
    }

    if (this.#isRecord(backends[safeKey])) {
      const backend = backends[safeKey];
      return {
        key: safeKey,
        found: true,
        source: 'backend',
        lineage: {
          protocol: safeKey,
          storageId: safeKey,
          normalizedStorageId: null,
          iterationId: null,
          pageIterationId: null,
          arrayRootPath: null,
          backendType: String(this.#safeRead(backend, 'type') || 'unknown')
        },
        backend: {
          protocol: safeKey,
          type: String(this.#safeRead(backend, 'type') || 'unknown'),
          initialized: Boolean(this.#safeRead(backend, 'initialized')),
          isLoading: Boolean(this.#safeRead(backend, 'isLoading'))
        }
      };
    }

    return {
      key: safeKey,
      found: false,
      source: null,
      lineage: null
    };
  }

  #getStatefulDrilldown(query?: string, limit?: number): DevtoolsStatefulDrilldown {
    const requested = String(query || '').trim();
    const requestedLower = requested.toLowerCase();
    const max = this.#toFiniteNumber(limit, true) ?? 80;
    const safeMax = Math.max(1, Math.min(250, max));

    const instancesMap = this.#getStatefulInstancesMap();
    const data = this.#asRecord(this.#storageService.data) || {};
    const arraySizes = this.#asRecord(this.#storageService.arraySizes) || {};
    const backends = this.#asRecord(this.#storageService.backends) || {};

    const matches: DevtoolsStatefulDrilldownMatch[] = [];
    const isMatch = (value: string): boolean => !requestedLower || value.toLowerCase().includes(requestedLower);

    Object.entries(instancesMap).forEach(([id, raw]) => {
      if (!this.#isRecord(raw) || !isMatch(id)) return;
      const instance = raw;
      matches.push({
        source: 'instance',
        key: id,
        lineage: this.#buildStatefulLineage(id),
        value: this.#sanitizeValue(this.#safeRead(instance, 'value')),
        savedValue: this.#sanitizeValue(this.#safeRead(instance, 'savedValue')),
        isChanged: Boolean(this.#safeRead(instance, 'isChanged')),
        isLoading: Boolean(this.#safeRead(instance, 'isLoading')),
        saveLock: Boolean(this.#safeRead(instance, '_saveLock'))
      });
    });

    Object.entries(data).forEach(([key, value]) => {
      if (!isMatch(key)) return;
      matches.push({
        source: 'data',
        key,
        lineage: this.#buildStatefulLineage(key),
        dataValue: this.#sanitizeValue(value)
      });
    });

    Object.entries(arraySizes).forEach(([rootPath, sizeRaw]) => {
      if (!isMatch(rootPath)) return;
      matches.push({
        source: 'array',
        key: rootPath,
        lineage: this.#buildStatefulLineage(rootPath),
        arraySize: Number(sizeRaw) || 0
      });
    });

    Object.entries(backends).forEach(([protocol, raw]) => {
      if (!this.#isRecord(raw) || !isMatch(protocol)) return;
      const backend = raw;
      matches.push({
        source: 'backend',
        key: protocol,
        lineage: {
          protocol,
          storageId: protocol,
          normalizedStorageId: null,
          iterationId: null,
          pageIterationId: null,
          arrayRootPath: null,
          backendType: String(this.#safeRead(backend, 'type') || 'unknown')
        },
        initialized: Boolean(this.#safeRead(backend, 'initialized')),
        isLoading: Boolean(this.#safeRead(backend, 'isLoading'))
      });
    });

    matches.sort((a, b) => a.key.localeCompare(b.key));

    return {
      query: requested,
      totalMatches: matches.length,
      matches: matches.slice(0, safeMax)
    };
  }

  #normalizeStatefulStorageId(storageId: string): string | null {
    const safeStorageId = String(storageId || '').trim();
    if (!safeStorageId) return null;

    try {
      const parsed = this.#storageInfoService.parse(safeStorageId);
      return parsed?.storageId || safeStorageId;
    } catch {
      return safeStorageId;
    }
  }

  #resolveFieldConfigId(fieldRef: string, fields: JsonRecord): string | null {
    const safeFieldRef = String(fieldRef || '').trim();
    if (!safeFieldRef) return null;

    if (fields[safeFieldRef]) return safeFieldRef;

    const fieldIterationToPage = safeFieldRef.replace(new RegExp(`\\${FIELD_ITERATION_SYMBOL}`, 'g'), PATH_PAGE_ITERATION_SYMBOL);
    if (fields[fieldIterationToPage]) return fieldIterationToPage;

    const pageIterationToField = safeFieldRef.replace(new RegExp(`\\${PATH_PAGE_ITERATION_SYMBOL}`, 'g'), FIELD_ITERATION_SYMBOL);
    if (fields[pageIterationToField]) return pageIterationToField;

    return null;
  }

  #extractStatefulRefsFromConfig(
    value: unknown,
    allowedProtocols: Set<string>,
    refs: Set<string>,
    depth = 0,
    maxDepth = 6
  ): void {
    if (depth > maxDepth || value == null) return;

    if (typeof value === 'string') {
      this.#extractStatefulRefsFromString(value, allowedProtocols, refs);
      return;
    }

    const nextDepth = depth + 1;
    if (Array.isArray(value)) {
      value.forEach((entry) => this.#extractStatefulRefsFromConfig(entry, allowedProtocols, refs, nextDepth, maxDepth));
      return;
    }

    const record = this.#asRecord(value);
    if (!record) return;
    Object.values(record).forEach((entry) => this.#extractStatefulRefsFromConfig(entry, allowedProtocols, refs, nextDepth, maxDepth));
  }

  #extractStatefulRefsFromString(value: string, allowedProtocols: Set<string>, refs: Set<string>): void {
    let index = 0;
    while (index < value.length) {
      const startCode = value.charCodeAt(index);
      if (!this.#isStatefulProtocolStartCode(startCode)) {
        index += 1;
        continue;
      }

      const protocolStart = index;
      index += 1;
      while (index < value.length && this.#isStatefulProtocolCode(value.charCodeAt(index))) {
        index += 1;
      }

      if (index >= value.length || value.charCodeAt(index) !== 58) {
        continue;
      }

      const protocol = value.slice(protocolStart, index);
      index += 1;
      const valueStart = index;
      while (index < value.length && !this.#isStatefulRefTokenTerminatorCode(value.charCodeAt(index))) {
        index += 1;
      }

      if (index <= valueStart) continue;

      const token = this.#trimStatefulRefTrailingPunctuation(value.slice(protocolStart, index));
      if (!token) continue;
      if (allowedProtocols.size && !allowedProtocols.has(protocol)) continue;

      refs.add(token);
    }
  }

  #isStatefulProtocolStartCode(code: number): boolean {
    return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
  }

  #isStatefulProtocolCode(code: number): boolean {
    return this.#isStatefulProtocolStartCode(code) || (code >= 48 && code <= 57) || code === 95 || code === 45;
  }

  #isStatefulRefTokenTerminatorCode(code: number): boolean {
    return code <= 32 || code === 34 || code === 39 || code === 41 || code === 44 || code === 93 || code === 96 || code === 125;
  }

  #trimStatefulRefTrailingPunctuation(token: string): string {
    let end = token.length;
    while (end > 0) {
      const code = token.charCodeAt(end - 1);
      if (code !== 44 && code !== 46 && code !== 59) break;
      end -= 1;
    }
    return end === token.length ? token : token.slice(0, end);
  }

  #statefulRefMatchesTarget(ref: string, lookup: DevtoolsStatefulValueLookup): boolean {
    const safeRef = String(ref || '').trim();
    if (!safeRef) return false;

    const targetKey = String(lookup.key || '').trim();
    const targetLineage = lookup.lineage;

    const refLineage = this.#buildStatefulLineage(safeRef);
    const refNormalized = refLineage.normalizedStorageId || this.#normalizeStatefulStorageId(safeRef);
    const targetNormalized = targetLineage?.normalizedStorageId || this.#normalizeStatefulStorageId(targetKey);

    if (safeRef === targetKey) return true;
    if (targetNormalized && refNormalized && refNormalized === targetNormalized) return true;
    if (targetNormalized && safeRef === targetNormalized) return true;
    if (targetLineage?.arrayRootPath && refLineage.arrayRootPath && refLineage.arrayRootPath === targetLineage.arrayRootPath) return true;
    if (targetLineage?.arrayRootPath && safeRef.startsWith(`${targetLineage.arrayRootPath}/`)) return true;

    return false;
  }

  #getStatefulLineageGraph(key: string, limit?: number): DevtoolsStatefulLineageGraph {
    const safeKey = String(key || '').trim();
    const lookup = this.#getStatefulDataValue(safeKey);
    const max = this.#toFiniteNumber(limit, true) ?? 120;
    const safeMax = Math.max(1, Math.min(500, max));

    const config = this.#asRecord(dependencies.config) || {};
    const flows = this.#asRecord(config.flows) || {};
    const pages = this.#asRecord(config.pages) || {};
    const cards = this.#asRecord(config.cards) || {};
    const fields = this.#asRecord(config.fields) || {};
    const workflowSpecificPages = this.#asRecord(config.workflowSpecificPages) || {};
    const workflowSpecificCards = this.#asRecord(config.workflowSpecificCards) || {};
    const workflowSpecificFields = this.#asRecord(config.workflowSpecificFields) || {};
    const storageConfig = this.#asRecord(config.storage) || {};
    const backendMap = this.#asRecord(this.#storageService.backends) || {};

    const allowedProtocols = new Set<string>([
      ...Object.keys(storageConfig),
      ...Object.keys(backendMap),
      ...(lookup.lineage?.protocol ? [lookup.lineage.protocol] : [])
    ]);

    const bindingMap = new Map<string, DevtoolsStatefulFieldBinding>();
    const bindingLocationKeys = new Map<string, Set<string>>();

    Object.entries(flows).forEach(([flowId, flowRaw]) => {
      const flow = this.#asRecord(flowRaw) || {};
      const flowPath = typeof flow.path === 'string' ? flow.path : null;
      const pageIds = Array.isArray(flow.pages) ? flow.pages.filter((pageId): pageId is string => typeof pageId === 'string') : [];

      pageIds.forEach((pageId) => {
        const pageBase = this.#asRecord(pages[pageId]);
        if (!pageBase) return;

        const pageOverride = this.#asRecord(workflowSpecificPages[`${flowId}/${pageId}`]) || {};
        const pageConfig = { ...pageBase, ...pageOverride };
        const pagePath = typeof pageConfig.path === 'string' ? pageConfig.path : null;
        const cardIds = Array.isArray(pageConfig.cards) ? pageConfig.cards.filter((cardId): cardId is string => typeof cardId === 'string') : [];

        cardIds.forEach((cardId) => {
          const cardBase = this.#asRecord(cards[cardId]);
          if (!cardBase) return;

          const cardOverride = this.#asRecord(workflowSpecificCards[`${flowId}/${cardId}`]) || {};
          const cardConfig = { ...cardBase, ...cardOverride };
          const fieldRefs = Array.isArray(cardConfig.fields) ? cardConfig.fields.filter((fieldRef): fieldRef is string => typeof fieldRef === 'string') : [];

          fieldRefs.forEach((fieldRef) => {
            const fieldId = this.#resolveFieldConfigId(fieldRef, fields);
            if (!fieldId) return;

            const fieldBase = this.#asRecord(fields[fieldId]);
            if (!fieldBase) return;

            const fieldOverride = this.#asRecord(workflowSpecificFields[`${flowId}/${fieldId}`]) || {};
            const fieldConfig = { ...fieldBase, ...fieldOverride };

            const refs = new Set<string>();
            this.#extractStatefulRefsFromConfig(fieldConfig, allowedProtocols, refs);
            if (!refs.size && typeof fieldConfig.storageId === 'string') {
              refs.add(fieldConfig.storageId);
            }

            refs.forEach((ref) => {
              if (!this.#statefulRefMatchesTarget(ref, lookup)) return;

              const refLineage = this.#buildStatefulLineage(ref);
              const normalizedRef = refLineage.normalizedStorageId || this.#normalizeStatefulStorageId(ref);
              const bindingKey = `${fieldId}::${normalizedRef || ref}`;
              const locationKey = `${flowId}/${pageId}/${cardId}/${fieldId}`;

              let binding = bindingMap.get(bindingKey);
              if (!binding) {
                binding = {
                  fieldId,
                  storageId: ref,
                  normalizedStorageId: normalizedRef,
                  protocol: refLineage.protocol,
                  backendType: refLineage.backendType,
                  locationCount: 0,
                  locations: []
                };
                bindingMap.set(bindingKey, binding);
                bindingLocationKeys.set(bindingKey, new Set<string>());
              }

              const locationSet = bindingLocationKeys.get(bindingKey);
              if (!locationSet?.has(locationKey)) {
                locationSet?.add(locationKey);
                binding.locations.push({
                  flowId,
                  flowPath,
                  pageId,
                  pagePath,
                  cardId,
                  fieldId
                });
              }
            });
          });
        });
      });
    });

    const allBindings = Array.from(bindingMap.values())
      .map((binding) => ({
        ...binding,
        locationCount: binding.locations.length,
        locations: [...binding.locations].sort((a, b) => {
          if (a.flowId !== b.flowId) return a.flowId.localeCompare(b.flowId);
          if (a.pageId !== b.pageId) return a.pageId.localeCompare(b.pageId);
          return a.cardId.localeCompare(b.cardId);
        })
      }))
      .sort((a, b) => {
        if (a.fieldId !== b.fieldId) return a.fieldId.localeCompare(b.fieldId);
        return (a.normalizedStorageId || a.storageId).localeCompare(b.normalizedStorageId || b.storageId);
      });

    return {
      key: safeKey,
      lookup,
      lineage: lookup.lineage,
      totalBindings: allBindings.length,
      bindings: allBindings.slice(0, safeMax)
    };
  }

  #recordStatefulWriteTrace(entry: Omit<DevtoolsStatefulWriteTrace, 'id' | 'timestamp' | 'callSite'>): void {
    if (!this.#statefulTracingEnabled()) return;
    this.#statefulWriteTrace.unshift({
      id: this.#uniqueId('state-write'),
      timestamp: new Date().toISOString(),
      callSite: this.#extractStatefulWriteCallSite(),
      ...entry,
      oldValue: this.#sanitizeValue(entry.oldValue),
      newValue: this.#sanitizeValue(entry.newValue),
      savedBefore: this.#sanitizeValue(entry.savedBefore),
      savedAfter: this.#sanitizeValue(entry.savedAfter),
      meta: entry.meta ? (this.#sanitizeValue(entry.meta) as Record<string, unknown>) : undefined
    });

    if (this.#statefulWriteTrace.length > MAX_STATEFUL_WRITE_TRACE) {
      this.#statefulWriteTrace.length = MAX_STATEFUL_WRITE_TRACE;
    }
  }

  #extractStatefulWriteCallSite(): string | null {
    try {
      const stack = new Error().stack || '';
      const lines = stack
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const candidate = lines.find(
        (line) => !line.includes('devtools-bridge.service') && !line.includes('StatefulValue.') && !line.startsWith('Error')
      );
      return candidate || null;
    } catch {
      return null;
    }
  }

  #captureStatefulInstanceSignatures(): Record<string, string> {
    const signatures: Record<string, string> = {};
    const instancesMap = this.#getStatefulInstancesMap();
    Object.entries(instancesMap).forEach(([id, raw]) => {
      if (!this.#isRecord(raw)) return;
      signatures[id] = this.#buildStatefulInstanceSignature(raw);
    });
    return signatures;
  }

  #buildStatefulInstanceSignature(instance: Record<string, unknown>): string {
    const payload = {
      value: this.#safeRead(instance, 'value'),
      savedValue: this.#safeRead(instance, 'savedValue'),
      isChanged: Boolean(this.#safeRead(instance, 'isChanged')),
      isLoading: Boolean(this.#safeRead(instance, 'isLoading')),
      saveLock: Boolean(this.#safeRead(instance, '_saveLock'))
    };
    try {
      return JSON.stringify(this.#sanitizeValue(payload)) || '';
    } catch {
      return String(payload.value);
    }
  }

  #diffStatefulInstanceSignatures(before: Record<string, string>, after: Record<string, string>): string[] {
    const keys = new Set<string>([...Object.keys(before), ...Object.keys(after)]);
    const changed: string[] = [];
    keys.forEach((key) => {
      if ((before[key] || '') !== (after[key] || '')) {
        changed.push(key);
      }
    });
    return changed.sort((a, b) => a.localeCompare(b));
  }

  #correlateWorkflowRequestState(id: string, beforeSignatures: Record<string, string>, startedAtIso: string): void {
    const requestStartMs = Date.parse(startedAtIso);
    const afterSignatures = this.#captureStatefulInstanceSignatures();
    const changedKeys = this.#diffStatefulInstanceSignatures(beforeSignatures, afterSignatures);
    const changedSet = new Set(changedKeys);

    const writeKeys = Array.from(
      new Set(
        this.#statefulWriteTrace
          .filter((entry) => {
            if (!entry.key) return false;
            if (!changedSet.has(entry.key)) return false;
            if (!Number.isFinite(requestStartMs)) return true;
            const entryMs = Date.parse(entry.timestamp);
            return Number.isFinite(entryMs) && entryMs >= requestStartMs;
          })
          .map((entry) => String(entry.key))
      )
    );

    this.#updateWorkflowRequest(id, {
      stateCorrelation: {
        correlatedAt: new Date().toISOString(),
        totalChanged: changedKeys.length,
        changedKeys: changedKeys.slice(0, 80),
        writeCount: writeKeys.length,
        writeKeys: writeKeys.slice(0, 80)
      }
    });
  }

  #getStatefulWriteTrace(limit?: number, key?: string): DevtoolsStatefulWriteTrace[] {
    const max = this.#toFiniteNumber(limit, true) ?? 150;
    const safeMax = Math.max(1, Math.min(MAX_STATEFUL_WRITE_TRACE, max));
    const keyFilter = String(key || '').trim();
    const filtered = keyFilter ? this.#statefulWriteTrace.filter((entry) => entry.key === keyFilter) : this.#statefulWriteTrace;
    return filtered.slice(0, safeMax).map((entry) => ({ ...entry }));
  }

  #buildWorkflowSummary(): DevtoolsWorkflowSummary {
    const config = this.#asRecord(dependencies.config) || {};

    const flows = this.#asRecord(config.flows) || {};
    const pages = this.#asRecord(config.pages) || {};
    const cards = this.#asRecord(config.cards) || {};
    const fields = this.#asRecord(config.fields) || {};

    let currentFlowId: string | null = null;
    let currentFlowPath: string | null = null;
    let currentPageId: string | null = null;
    let currentPagePath: string | null = null;

    try {
      const currentFlow = this.#workflowService.currentFlow as unknown as Record<string, unknown>;
      if (currentFlow) {
        currentFlowId = String(currentFlow.id || null);
        const flowConfig = this.#asRecord(currentFlow.config);
        currentFlowPath = flowConfig?.path ? String(flowConfig.path) : null;
      }
    } catch {
      currentFlowId = null;
    }

    try {
      const currentPage = this.#workflowService.currentPage as unknown as Record<string, unknown>;
      if (currentPage) {
        currentPageId = String(currentPage.id || null);
        currentPagePath = currentPage.path ? String(currentPage.path) : null;
      }
    } catch {
      currentPageId = null;
    }

    const flowSummaries: DevtoolsWorkflowFlowSummary[] = Object.entries(flows)
      .map(([id, flowRaw]) => {
        const flow = this.#asRecord(flowRaw) || {};
        const flowPages = Array.isArray(flow.pages) ? flow.pages.filter((page) => typeof page === 'string') : [];

        let runtimeVisiblePageCount: number | null = null;
        let runtimeCompleted: boolean | null = null;

        try {
          const runtimeFlow = this.#workflowService.getFlowById(id) as unknown as Record<string, unknown> | undefined;
          const runtimePages = runtimeFlow && Array.isArray(runtimeFlow.pages) ? runtimeFlow.pages : null;
          runtimeVisiblePageCount = runtimePages ? runtimePages.length : null;
          runtimeCompleted = typeof runtimeFlow?.isCompleted === 'boolean' ? runtimeFlow.isCompleted : null;
        } catch {
          runtimeVisiblePageCount = null;
        }

        return {
          id,
          path: String(flow.path || id),
          pageCount: flowPages.length,
          firstPage: flowPages[0] || null,
          lastPage: flowPages[flowPages.length - 1] || null,
          runtimeVisiblePageCount,
          runtimeCompleted
        };
      })
      .sort((a, b) => a.id.localeCompare(b.id));

    const issues = this.#validateWorkflowModel(flows, pages, cards, fields);
    const graph = this.#buildWorkflowGraph(flows, pages, cards, fields);

    return {
      currentFlowId,
      currentFlowPath,
      currentPageId,
      currentPagePath,
      routePrefix: this.#workflowService.routePrefix || null,
      flowCount: Object.keys(flows).length,
      pageCount: Object.keys(pages).length,
      cardCount: Object.keys(cards).length,
      fieldCount: Object.keys(fields).length,
      flowSummaries: flowSummaries.slice(0, 120),
      validation: {
        checkedAt: new Date().toISOString(),
        issueCount: issues.length,
        issues: issues.slice(0, 200)
      },
      graph,
      requests: this.#getWorkflowRequests(80)
    };
  }

  #simulateWorkflowPath(flowId: string, mockValues?: Record<string, unknown>): DevtoolsWorkflowSimulation {
    const safeFlowId = String(flowId || '').trim();
    const config = this.#asRecord(dependencies.config) || {};
    const flows = this.#asRecord(config.flows) || {};
    const pages = this.#asRecord(config.pages) || {};
    const cards = this.#asRecord(config.cards) || {};

    const flow = safeFlowId ? this.#asRecord(flows[safeFlowId]) : null;
    const pageIds = Array.isArray(flow?.pages) ? flow.pages.filter((pageId): pageId is string => typeof pageId === 'string') : [];

    let currentPageId: string | null = null;
    try {
      const currentPage = this.#workflowService.currentPage as unknown as Record<string, unknown>;
      if (currentPage?.id) {
        currentPageId = String(currentPage.id);
      }
    } catch {
      currentPageId = null;
    }

    let nextPageId: string | null = pageIds[0] || null;
    if (currentPageId) {
      const currentIndex = pageIds.indexOf(currentPageId);
      if (currentIndex > -1 && currentIndex + 1 < pageIds.length) {
        nextPageId = pageIds[currentIndex + 1];
      }
    }

    const mockRecord = this.#isRecord(mockValues) ? mockValues : {};
    const mockKeys = Object.keys(mockRecord);
    const allFieldIds = new Set<string>();
    const pageSummaries: DevtoolsWorkflowSimulationPage[] = pageIds.map((pageId, index) => {
      const page = this.#asRecord(pages[pageId]) || {};
      const cardIds = Array.isArray(page.cards) ? page.cards.filter((cardId): cardId is string => typeof cardId === 'string') : [];
      let fieldCount = 0;
      cardIds.forEach((cardId) => {
        const card = this.#asRecord(cards[cardId]) || {};
        const fieldIds = Array.isArray(card.fields) ? card.fields.filter((fieldId): fieldId is string => typeof fieldId === 'string') : [];
        fieldCount += fieldIds.length;
        fieldIds.forEach((fieldId) => allFieldIds.add(fieldId));
      });

      return {
        id: pageId,
        path: typeof page.path === 'string' ? page.path : null,
        index,
        cardCount: cardIds.length,
        fieldCount,
        isCurrent: currentPageId === pageId
      };
    });

    const missingMockFields = Array.from(allFieldIds).filter((fieldId) => !Object.prototype.hasOwnProperty.call(mockRecord, fieldId));
    const matchedMockFields = mockKeys.filter((fieldId) => allFieldIds.has(fieldId));

    const ruleOutcomes: DevtoolsWorkflowIssue[] = [];
    ruleOutcomes.push({
      severity: flow ? 'warn' : 'error',
      code: flow ? 'SIM_FLOW_LOADED' : 'SIM_FLOW_MISSING',
      message: flow ? `Loaded flow ${safeFlowId} for simulation.` : `Flow ${safeFlowId || '<empty>'} was not found.`,
      ref: safeFlowId || undefined
    });
    ruleOutcomes.push({
      severity: 'warn',
      code: missingMockFields.length ? 'SIM_MOCK_FIELDS_MISSING' : 'SIM_MOCK_FIELDS_COMPLETE',
      message: missingMockFields.length
        ? `${missingMockFields.length} flow fields do not have mock values.`
        : 'All flow fields have mock values.',
      ref: missingMockFields[0]
    });

    return {
      flowId: safeFlowId,
      valid: Boolean(flow),
      flowPath: typeof flow?.path === 'string' ? flow.path : null,
      routePrefix: this.#workflowService.routePrefix || null,
      currentPageId,
      nextPageId,
      pageCount: pageIds.length,
      mockedFieldCount: matchedMockFields.length,
      missingMockFieldCount: missingMockFields.length,
      pages: pageSummaries,
      ruleOutcomes
    };
  }

  #getWorkflowDriftReport(limit?: number): DevtoolsWorkflowDriftReport {
    const max = this.#toFiniteNumber(limit, true) ?? 300;
    const safeMax = Math.max(1, Math.min(1000, max));
    const config = this.#asRecord(dependencies.config) || {};
    const flows = this.#asRecord(config.flows) || {};
    const pages = this.#asRecord(config.pages) || {};
    const cards = this.#asRecord(config.cards) || {};
    const fields = this.#asRecord(config.fields) || {};
    const workflowSpecificFields = this.#asRecord(config.workflowSpecificFields) || {};
    const storageConfig = this.#asRecord(config.storage) || {};
    const backendMap = this.#asRecord(this.#storageService.backends) || {};

    const issues = [...this.#validateWorkflowModel(flows, pages, cards, fields)];
    const allowedProtocols = new Set<string>([...Object.keys(storageConfig), ...Object.keys(backendMap)]);

    Object.entries(fields).forEach(([fieldId, fieldRaw]) => {
      const fieldBase = this.#asRecord(fieldRaw) || {};
      const refs = new Set<string>();
      this.#extractStatefulRefsFromConfig(fieldBase, new Set<string>(), refs);
      Object.values(workflowSpecificFields).forEach((workflowFieldRaw) => {
        const workflowField = this.#asRecord(workflowFieldRaw) || {};
        if (String(workflowField.id || '') !== fieldId) return;
        this.#extractStatefulRefsFromConfig(workflowField, new Set<string>(), refs);
      });

      refs.forEach((ref) => {
        const protocol = ref.split(':')[0];
        if (allowedProtocols.has(protocol)) return;
        issues.push({
          severity: 'warn',
          code: 'FIELD_STORAGE_PROTOCOL_UNKNOWN',
          message: `Field ${fieldId} references unknown storage protocol "${protocol}".`,
          ref
        });
      });
    });

    return {
      checkedAt: new Date().toISOString(),
      issueCount: issues.length,
      issues: issues.slice(0, safeMax)
    };
  }

  #getHealthChecks(): DevtoolsHealthReport {
    const checks: DevtoolsHealthCheck[] = [];
    const stateful = this.#buildStatefulSummary();
    const workflow = this.#buildWorkflowSummary();
    const drift = this.#getWorkflowDriftReport(200);
    const backends = stateful.backends || [];
    const workflowRequests = this.#getWorkflowRequests(60);

    checks.push({
      id: 'stateful.backends.present',
      status: backends.length ? 'pass' : 'fail',
      message: backends.length ? `Detected ${backends.length} stateful backends.` : 'No stateful backends detected.'
    });

    checks.push({
      id: 'stateful.arrayRoots.volume',
      status: stateful.arrayRootCount > 1000 ? 'warn' : 'pass',
      message:
        stateful.arrayRootCount > 1000
          ? `High array root count detected (${stateful.arrayRootCount}).`
          : `Array roots in expected range (${stateful.arrayRootCount}).`
    });

    checks.push({
      id: 'stateful.changed.stale',
      status: stateful.changedCount > 250 ? 'warn' : 'pass',
      message:
        stateful.changedCount > 250
          ? `Large changed stateful set (${stateful.changedCount}) may indicate stale unsaved state.`
          : `Changed stateful count healthy (${stateful.changedCount}).`
    });

    checks.push({
      id: 'workflow.validation.issues',
      status: workflow.validation.issueCount > 0 ? 'warn' : 'pass',
      message:
        workflow.validation.issueCount > 0
          ? `Workflow model has ${workflow.validation.issueCount} validation issues.`
          : 'Workflow model validation has no issues.'
    });

    checks.push({
      id: 'workflow.drift.issues',
      status: drift.issueCount > 0 ? 'warn' : 'pass',
      message: drift.issueCount > 0 ? `Workflow drift detector found ${drift.issueCount} issues.` : 'No workflow drift detected.'
    });

    const failingRequests = workflowRequests.filter((request) => !request.ok).length;
    checks.push({
      id: 'workflow.requests.failures',
      status: failingRequests > 0 ? 'warn' : 'pass',
      message: failingRequests > 0 ? `${failingRequests} workflow/config request failures detected.` : 'No workflow/config request failures detected.'
    });

    const summary = checks.reduce(
      (acc, check) => {
        acc[check.status] += 1;
        return acc;
      },
      { pass: 0, warn: 0, fail: 0 }
    );

    return {
      checkedAt: new Date().toISOString(),
      summary,
      checks
    };
  }

  #getBridgeContract(): DevtoolsBridgeContract {
    return {
      schemaVersion: DEVTOOLS_BRIDGE_SCHEMA_VERSION,
      bridgeVersion: DEVTOOLS_BRIDGE_VERSION,
      methods: [
        'getSnapshot',
        'getSnapshotLite',
        'getStatefulSummary',
        'getWorkflowSummary',
        'getStateServices',
        'getCaptureSettings',
        'setStatefulTracing',
        'setWorkflowCorrelation',
        'setWorkflowPayloadCapture',
        'getStatefulDrilldown',
        'getStatefulDataValue',
        'getStatefulLineageGraph',
        'getStatefulWriteTrace',
        'getSawdustState',
        'getSawdustLogs',
        'clearSawdustLogs',
        'setSawdustLogLevel',
        'getWorkflowRequests',
        'clearWorkflowRequests',
        'simulateWorkflowPath',
        'getWorkflowDriftReport',
        'getHealthChecks',
        'getBridgeContract',
        'setFeatureFlagOverride',
        'clearFeatureFlagOverride',
        'clearFeatureFlagOverrides',
        'setPermissionSimulation',
        'clearPermissionSimulation'
      ],
      snapshotKeys: ['bridge', 'app', 'router', 'user', 'session', 'lender', 'features', 'sawdust', 'stateful', 'workflow', 'config', 'stateServices'],
      workflowRequestKeys: [
        'id',
        'timestamp',
        'transport',
        'method',
        'url',
        'status',
        'durationMs',
        'ok',
        'error',
        'payloadSummary',
        'stateCorrelation'
      ],
      statefulWriteTraceKeys: [
        'id',
        'timestamp',
        'key',
        'actor',
        'action',
        'oldValue',
        'newValue',
        'savedBefore',
        'savedAfter',
        'callSite',
        'meta'
      ]
    };
  }

  #getCaptureSettings(): DevtoolsCaptureSettings {
    return {
      statefulTracingEnabled: this.#statefulTracingEnabled(),
      workflowCorrelationEnabled: this.#workflowCorrelationEnabled(),
      workflowPayloadCaptureEnabled: this.#workflowPayloadCaptureEnabled()
    };
  }

  #setStatefulTracing(enabled: boolean): boolean {
    this.#statefulTracingEnabled.set(Boolean(enabled));
    return this.#statefulTracingEnabled();
  }

  #setWorkflowCorrelation(enabled: boolean): boolean {
    this.#workflowCorrelationEnabled.set(Boolean(enabled));
    return this.#workflowCorrelationEnabled();
  }

  #setWorkflowPayloadCapture(enabled: boolean): boolean {
    this.#workflowPayloadCaptureEnabled.set(Boolean(enabled));
    return this.#workflowPayloadCaptureEnabled();
  }

  #buildWorkflowGraph(flows: JsonRecord, pages: JsonRecord, cards: JsonRecord, fields: JsonRecord): DevtoolsWorkflowGraph {
    const referencedPages = new Set<string>();
    const referencedCards = new Set<string>();
    const referencedFields = new Set<string>();

    const pagesById: Record<string, DevtoolsWorkflowGraphPage> = {};
    const cardsById: Record<string, DevtoolsWorkflowGraphCard> = {};

    Object.entries(cards).forEach(([cardId, rawCard]) => {
      const card = this.#asRecord(rawCard) || {};
      const fieldIds = Array.isArray(card.fields) ? card.fields.filter((fieldId): fieldId is string => typeof fieldId === 'string') : [];
      const missingFieldIds = fieldIds.filter((fieldId) => !fields[fieldId]);

      cardsById[cardId] = {
        id: cardId,
        fieldIds,
        missingFieldIds
      };
    });

    Object.entries(pages).forEach(([pageId, rawPage]) => {
      const page = this.#asRecord(rawPage) || {};
      const cardIds = Array.isArray(page.cards) ? page.cards.filter((cardId): cardId is string => typeof cardId === 'string') : [];
      const missingCardIds = cardIds.filter((cardId) => !cards[cardId]);

      pagesById[pageId] = {
        id: pageId,
        path: typeof page.path === 'string' ? page.path : null,
        type: typeof page.type === 'string' ? page.type : 'page',
        cardIds,
        missingCardIds
      };
    });

    const flowGraph: DevtoolsWorkflowGraphFlow[] = Object.entries(flows)
      .map(([flowId, rawFlow]) => {
        const flow = this.#asRecord(rawFlow) || {};
        const pageIds = Array.isArray(flow.pages) ? flow.pages.filter((pageId): pageId is string => typeof pageId === 'string') : [];
        const missingPageIds = pageIds.filter((pageId) => !pages[pageId]);

        return {
          id: flowId,
          path: typeof flow.path === 'string' ? flow.path : flowId,
          pageIds,
          missingPageIds
        };
      })
      .sort((a, b) => a.id.localeCompare(b.id));

    flowGraph.forEach((flow) => {
      flow.pageIds.forEach((pageId) => {
        if (!pagesById[pageId]) return;
        referencedPages.add(pageId);
        pagesById[pageId].cardIds.forEach((cardId) => {
          if (!cardsById[cardId]) return;
          referencedCards.add(cardId);
          cardsById[cardId].fieldIds.forEach((fieldId) => {
            if (!fields[fieldId]) return;
            referencedFields.add(fieldId);
          });
        });
      });
    });

    return {
      flows: flowGraph,
      pagesById,
      cardsById,
      unreachablePageIds: Object.keys(pages).filter((pageId) => !referencedPages.has(pageId)).sort((a, b) => a.localeCompare(b)),
      unreachableCardIds: Object.keys(cards).filter((cardId) => !referencedCards.has(cardId)).sort((a, b) => a.localeCompare(b)),
      unreachableFieldIds: Object.keys(fields).filter((fieldId) => !referencedFields.has(fieldId)).sort((a, b) => a.localeCompare(b))
    };
  }

  #validateWorkflowModel(flows: JsonRecord, pages: JsonRecord, cards: JsonRecord, fields: JsonRecord): DevtoolsWorkflowIssue[] {
    const issues: DevtoolsWorkflowIssue[] = [];

    const referencedPages = new Set<string>();
    const referencedCards = new Set<string>();
    const referencedFields = new Set<string>();

    Object.entries(flows).forEach(([flowId, flowRaw]) => {
      const flow = this.#asRecord(flowRaw);
      if (!flow) {
        issues.push({
          severity: 'error',
          code: 'FLOW_INVALID',
          message: `Flow ${flowId} config is not an object.`,
          ref: flowId
        });
        return;
      }

      if (!Array.isArray(flow.pages)) {
        issues.push({
          severity: 'error',
          code: 'FLOW_PAGES_NOT_ARRAY',
          message: `Flow ${flowId} has a non-array pages property.`,
          ref: flowId
        });
        return;
      }

      if (!flow.pages.length) {
        issues.push({
          severity: 'warn',
          code: 'FLOW_EMPTY',
          message: `Flow ${flowId} has no pages.`,
          ref: flowId
        });
      }

      flow.pages.forEach((pageIdRaw, index) => {
        if (typeof pageIdRaw !== 'string' || !pageIdRaw) {
          issues.push({
            severity: 'error',
            code: 'FLOW_PAGE_ID_INVALID',
            message: `Flow ${flowId} page reference at index ${index} is invalid.`,
            ref: flowId
          });
          return;
        }

        referencedPages.add(pageIdRaw);

        if (!pages[pageIdRaw]) {
          issues.push({
            severity: 'error',
            code: 'FLOW_PAGE_MISSING',
            message: `Flow ${flowId} references missing page ${pageIdRaw}.`,
            ref: `${flowId}:${pageIdRaw}`
          });
        }
      });
    });

    Object.entries(pages).forEach(([pageId, pageRaw]) => {
      const page = this.#asRecord(pageRaw);
      if (!page) {
        issues.push({
          severity: 'error',
          code: 'PAGE_INVALID',
          message: `Page ${pageId} config is not an object.`,
          ref: pageId
        });
        return;
      }

      const pageType = String(page.type || 'page').toLowerCase();
      const requiresCards = pageType === 'page';

      if (requiresCards && !Array.isArray(page.cards)) {
        issues.push({
          severity: 'error',
          code: 'PAGE_CARDS_NOT_ARRAY',
          message: `Page ${pageId} has a non-array cards property.`,
          ref: pageId
        });
        return;
      }

      if (Array.isArray(page.cards)) {
        page.cards.forEach((cardIdRaw, index) => {
          if (typeof cardIdRaw !== 'string' || !cardIdRaw) {
            issues.push({
              severity: 'error',
              code: 'PAGE_CARD_ID_INVALID',
              message: `Page ${pageId} card reference at index ${index} is invalid.`,
              ref: pageId
            });
            return;
          }

          referencedCards.add(cardIdRaw);

          if (!cards[cardIdRaw]) {
            issues.push({
              severity: 'error',
              code: 'PAGE_CARD_MISSING',
              message: `Page ${pageId} references missing card ${cardIdRaw}.`,
              ref: `${pageId}:${cardIdRaw}`
            });
          }
        });
      }
    });

    Object.entries(cards).forEach(([cardId, cardRaw]) => {
      const card = this.#asRecord(cardRaw);
      if (!card) {
        issues.push({
          severity: 'error',
          code: 'CARD_INVALID',
          message: `Card ${cardId} config is not an object.`,
          ref: cardId
        });
        return;
      }

      if (!Array.isArray(card.fields)) {
        issues.push({
          severity: 'error',
          code: 'CARD_FIELDS_NOT_ARRAY',
          message: `Card ${cardId} has a non-array fields property.`,
          ref: cardId
        });
        return;
      }

      card.fields.forEach((fieldIdRaw, index) => {
        if (typeof fieldIdRaw !== 'string' || !fieldIdRaw) {
          issues.push({
            severity: 'error',
            code: 'CARD_FIELD_ID_INVALID',
            message: `Card ${cardId} field reference at index ${index} is invalid.`,
            ref: cardId
          });
          return;
        }

        referencedFields.add(fieldIdRaw);

        if (!fields[fieldIdRaw]) {
          issues.push({
            severity: 'error',
            code: 'CARD_FIELD_MISSING',
            message: `Card ${cardId} references missing field ${fieldIdRaw}.`,
            ref: `${cardId}:${fieldIdRaw}`
          });
        }
      });
    });

    Object.keys(pages).forEach((pageId) => {
      if (!referencedPages.has(pageId)) {
        issues.push({
          severity: 'warn',
          code: 'PAGE_ORPHANED',
          message: `Page ${pageId} is not referenced by any flow.`,
          ref: pageId
        });
      }
    });

    Object.keys(cards).forEach((cardId) => {
      if (!referencedCards.has(cardId)) {
        issues.push({
          severity: 'warn',
          code: 'CARD_ORPHANED',
          message: `Card ${cardId} is not referenced by any page.`,
          ref: cardId
        });
      }
    });

    Object.keys(fields).forEach((fieldId) => {
      if (!referencedFields.has(fieldId)) {
        issues.push({
          severity: 'warn',
          code: 'FIELD_ORPHANED',
          message: `Field ${fieldId} is not referenced by any card.`,
          ref: fieldId
        });
      }
    });

    return issues;
  }

  #getWorkflowRequests(limit?: number): DevtoolsWorkflowRequest[] {
    const max = this.#toFiniteNumber(limit, true) ?? 80;
    const safeMax = Math.max(1, Math.min(MAX_WORKFLOW_REQUESTS, max));
    return this.#workflowRequests.slice(0, safeMax).map((entry) => ({ ...entry }));
  }

  #clearWorkflowRequests(): void {
    this.#workflowRequests.length = 0;
  }

  #readFeatureToggles(): Record<string, boolean> {
    const toggles: Record<string, boolean> = {};
    const flags = Array.isArray(this.#featureFlagService.featureFlags) ? this.#featureFlagService.featureFlags : [];

    flags.forEach((flag) => {
      if (!flag?.name) return;
      toggles[String(flag.name)] = Boolean(flag.isEnabled);
    });

    return toggles;
  }

  #extractPermissionsFromToken(): Record<string, string[]> {
    const decoded = this.#decodeAccessToken();
    if (!decoded) return {};

    const mapped = this.#extractPermissionMapFromClaims(decoded);
    if (Object.keys(mapped).length) {
      return mapped;
    }

    const claim = decoded.permissions;
    if (!Array.isArray(claim)) return {};

    const permissions: Record<string, string[]> = {};
    claim.forEach((value) => {
      if (typeof value !== 'string' || !value.trim()) return;
      const parsed = this.#parsePermissionEntry(value);
      const existing = permissions[parsed.key] ?? [];
      if (!existing.includes(parsed.action)) {
        existing.push(parsed.action);
      }
      existing.sort((a, b) => a.localeCompare(b));
      permissions[parsed.key] = existing;
    });

    return permissions;
  }

  #extractPermissionMapFromClaims(claims: Record<string, unknown>): Record<string, string[]> {
    const mapLikeKeys = ['appPartPermissions', 'permissionsByAppPart', 'permissionMap'];
    for (const key of mapLikeKeys) {
      const candidate = claims[key];
      if (!this.#isRecord(candidate)) continue;
      const parsed = this.#normalizePermissionMap(candidate);
      if (Object.keys(parsed).length) return parsed;
    }

    if (this.#isRecord(claims.permissions)) {
      return this.#normalizePermissionMap(claims.permissions);
    }

    return {};
  }

  #normalizePermissionMap(input: Record<string, unknown>): Record<string, string[]> {
    const normalized: Record<string, string[]> = {};

    Object.entries(input).forEach(([key, value]) => {
      const permissionKey = String(key || '').trim();
      if (!permissionKey) return;

      const actions = this.#toStringArray(value)
        .map((action) => this.#normalizeAction(action))
        .filter((action) => !!action);

      const unique = Array.from(new Set(actions)).sort((a, b) => a.localeCompare(b));
      normalized[permissionKey] = unique;
    });

    return normalized;
  }

  #toStringArray(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.filter((entry): entry is string => typeof entry === 'string');
    }

    if (typeof value === 'string') {
      if (!value.trim()) return [];
      return value
        .split(/[|,]/)
        .map((entry) => entry.trim())
        .filter(Boolean);
    }

    return [];
  }

  #parsePermissionEntry(value: string): { key: string; action: string } {
    const cleaned = value.trim();
    const segments = cleaned.split(/[.:/]/).filter(Boolean);

    if (segments.length < 2) {
      return { key: cleaned, action: 'View' };
    }

    const rawAction = segments[segments.length - 1];
    if (!this.#isKnownAction(rawAction)) {
      return { key: cleaned, action: 'View' };
    }

    return {
      key: segments.slice(0, -1).join('.'),
      action: this.#normalizeAction(rawAction)
    };
  }

  #normalizeAction(value: string): string {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return '';

    const knownActions: Record<string, string> = {
      view: 'View',
      read: 'Read',
      edit: 'Edit',
      write: 'Write',
      create: 'Create',
      add: 'Add',
      delete: 'Delete',
      remove: 'Remove',
      publish: 'Publish',
      approve: 'Approve',
      execute: 'Execute'
    };

    return knownActions[normalized] ?? value.trim();
  }

  #isKnownAction(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === 'view' ||
      normalized === 'read' ||
      normalized === 'edit' ||
      normalized === 'write' ||
      normalized === 'create' ||
      normalized === 'add' ||
      normalized === 'delete' ||
      normalized === 'remove' ||
      normalized === 'publish' ||
      normalized === 'approve' ||
      normalized === 'execute'
    );
  }

  #decodeAccessToken(): Record<string, unknown> | null {
    const accessToken = this.#getAccessToken();
    if (!accessToken) return null;

    try {
      const decoded = this.#tokenService.decodeToken(accessToken);
      return this.#isRecord(decoded) ? decoded : null;
    } catch {
      return null;
    }
  }

  #getAccessToken(): string | null {
    const cached = this.#tokenService.getCachedToken();
    if (cached?.access_token) {
      return cached.access_token;
    }

    return this.#userService.token ?? null;
  }

  #buildSession(config: AppConfig | null): DevtoolsSessionInfo {
    const timeoutSeconds = this.#toFiniteNumber(config?.adminPortalSessionTimeoutInSeconds, true);
    const timeoutDurationMs = timeoutSeconds !== null ? this.#normalizeTimeout(timeoutSeconds) : null;

    const decoded = this.#decodeAccessToken();
    const expClaim = decoded?.exp;
    const tokenExp = this.#toFiniteNumber(expClaim, false);

    let expiresAtMs: number | null = null;
    if (tokenExp !== null) {
      expiresAtMs = this.#tokenService.tokenExpiryHelper(tokenExp);
    }

    const expiresInMs = expiresAtMs !== null ? Math.max(0, expiresAtMs - Date.now()) : null;
    const expiresInSeconds = expiresInMs !== null ? Math.floor(expiresInMs / 1000) : null;

    return {
      expiresAt: expiresAtMs ? new Date(expiresAtMs).toISOString() : null,
      expiresInMs,
      expiresInSeconds,
      timeoutDurationMs,
      timeoutDurationSeconds: timeoutSeconds,
      isExpired: expiresInMs !== null ? expiresInMs <= 0 : false
    };
  }

  #toFiniteNumber(value: unknown, positiveOnly: boolean): number | null {
    if (value === null || value === undefined) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    if (positiveOnly && parsed <= 0) return null;
    return parsed;
  }

  #normalizeTimeout(timeoutSeconds: number): number {
    if (timeoutSeconds > 100000) return timeoutSeconds;
    return Math.round(timeoutSeconds * 1000);
  }

  #mapRoute(route: ActivatedRouteSnapshot | null): DevtoolsRouteSnapshot | null {
    if (!route) return null;

    return {
      path: route.routeConfig?.path ?? '',
      outlet: route.outlet,
      component: typeof route.component === 'function' ? route.component.name : null,
      params: route.params ?? {},
      data: route.data ?? {},
      children: (route.children || []).reduce<DevtoolsRouteSnapshot[]>((acc, child) => {
        const mapped = this.#mapRoute(child);
        if (mapped) {
          acc.push(mapped);
        }
        return acc;
      }, [])
    };
  }

  #mapLender(lender: LenderConfiguration | null): LenderSummary | null {
    if (!lender) return null;

    return {
      lenderCode: lender.lenderCode,
      lenderName: lender.lenderName,
      consumerLoanCode: lender.consumerLoanCode
    };
  }

  #resolveApiBaseUrl(config: AppConfig | null): string | null {
    if (config?.consumerPortalApi?.apiBaseURL) {
      return config.consumerPortalApi.apiBaseURL;
    }

    try {
      const resolved = this.#runtimeConfigService.getDXAPIBaseUrl();
      return resolved || null;
    } catch {
      return null;
    }
  }

  #sanitizeConfig(config: AppConfig | null): DevtoolsConfig | null {
    if (!config) return null;

    return {
      tenantId: config.tenantId,
      isProd: config.isProd,
      arcDXTokenStorageType: config.arcDXTokenStorageType,
      adminPortalTokenStorageType: config.adminPortalTokenStorageType,
      adminPortalSessionTimeoutInSeconds: config.adminPortalSessionTimeoutInSeconds,
      versionCheckMs: config.versionCheckMs,
      dataDog: config.dataDog
        ? {
            env: config.dataDog.env,
            service: config.dataDog.service,
            site: config.dataDog.site,
            version: config.dataDog.version
          }
        : undefined,
      consumerPortalApi: config.consumerPortalApi
        ? {
            domain: config.consumerPortalApi.domain,
            apiBaseURL: config.consumerPortalApi.apiBaseURL,
            routeScope: config.consumerPortalApi.routeScope,
            version: config.consumerPortalApi.version,
            api: config.consumerPortalApi.api,
            suffix: config.consumerPortalApi.suffix
          }
        : undefined,
      oid: config.oid
        ? {
            apiBaseURL: config.oid.apiBaseURL,
            oidConfigName: config.oid.oidConfigName,
            adminPortalOidConfigName: config.oid.adminPortalOidConfigName
          }
        : undefined
    };
  }

  #toSawdustLogEntry(event: LogEvent): DevtoolsSawdustLogEntry {
    return {
      id: this.#uniqueId('log'),
      level: String(event.level || 'info').toLowerCase(),
      timestamp: event.timestamp || new Date().toISOString(),
      name: event.name,
      message: event.message,
      context: this.#sanitizeValue(event.context),
      metadata: this.#sanitizeValue(event.metadata),
      error: this.#sanitizeValue(event.error)
    };
  }

  #sanitizeValue(value: unknown): unknown {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack
      };
    }

    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return String(value);
    }
  }

  #logLevelName(level: number): string {
    if (level === LogLevel.debug) return 'debug';
    if (level === LogLevel.info) return 'info';
    if (level === LogLevel.warn) return 'warn';
    if (level === LogLevel.error) return 'error';
    return `unknown(${level})`;
  }

  #countRecordKeys(value: unknown): number {
    const record = this.#asRecord(value);
    return record ? Object.keys(record).length : 0;
  }

  #asRecord(value: unknown): JsonRecord | null {
    if (!this.#isRecord(value)) return null;
    return value;
  }

  #isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  #safeRead(source: Record<string, unknown>, key: string): unknown {
    try {
      return source[key];
    } catch {
      return null;
    }
  }

  #toErrorString(error: unknown): string {
    if (error instanceof Error) return error.message;
    try {
      return String(error);
    } catch {
      return 'unknown error';
    }
  }

  #uniqueId(prefix: string): string {
    const entropy = this.#secureIdEntropy();
    this.#uniqueIdCounter = (this.#uniqueIdCounter + 1) % 65536;
    const counter = this.#uniqueIdCounter.toString(16).padStart(4, '0');
    return `${prefix}-${Date.now()}-${entropy}-${counter}`;
  }

  #secureIdEntropy(): string {
    try {
      const cryptoApi = globalThis.crypto;
      if (!cryptoApi) {
        return Date.now().toString(16).padStart(12, '0').slice(-12);
      }

      if (typeof cryptoApi.randomUUID === 'function') {
        return cryptoApi.randomUUID().split('-').join('').slice(0, 12);
      }

      if (typeof cryptoApi.getRandomValues === 'function') {
        const bytes = new Uint8Array(6);
        cryptoApi.getRandomValues(bytes);
        return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      }
    } catch {
      // ignore entropy generation failures and use a deterministic fallback
    }

    return Date.now().toString(16).padStart(12, '0').slice(-12);
  }
}
